// React and React Router imports
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AdminSessionModel } from "features/admin/core/domain/admin-session.model";

// Material UI Core Components
import {
  Box,
  Button,
  Paper,
  Fade,
  Step,
  StepLabel,
  Stepper,
  Typography,
  TextField,
  Grid,
  MenuItem,
  IconButton,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  TextareaAutosize,
  SelectChangeEvent,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  CircularProgress,
} from "@mui/material";

// Material UI Icons
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EventNoteIcon from "@mui/icons-material/EventNote";
import TodayIcon from "@mui/icons-material/Today";
import DateRangeIcon from "@mui/icons-material/DateRange";
import CloseIcon from "@mui/icons-material/Close";
import LaunchIcon from "@mui/icons-material/Launch";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { FcCheckmark } from "react-icons/fc";

// Redux imports
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  dailyStaticData,
  selectDailyStaticData,
} from "../slices/daily-static-data.slice";
import { formList } from "../slices/form-list.slice";

// Custom components
import TransferList from "../components/transferlist";
import NumberInput from "../components/ms6-increment-decrement";
import StockoutInput from "../components/ms6-stockout-input";

// Utilities
import { dateSetup } from "features/stock-ordering/presentation/components/stock-ordering-utils";
import { StringNullableChain } from "lodash";
import { dailyProduct } from "../slices/daily-product-data.slice";

// Define types for form data
export interface AttendanceFormRow {
  id: string;
  store_code: string;
  attendance_date: string;
  shift: string;
  employee_name: string;
  attendance: string;
}

export interface IncidentFormRow {
  id: string;
  store_code: string;
  dateTimeReported: string;
  reportedTo: string;
  dateTimeOfIncident: string;
  reportedBy: string;
  peopleInvolved: string;
  actionTaken: string;
  remarks: string;
  status: string;
  employee_name: string;
}

export interface InventoryFormRow {
  id: string;
  store_code: string;
  date: string;
  itemDescription: string;
  criticalLevel: string;
  quantity: string;
  expiryDate: string;
}

export interface EfficiencyFormRow {
  id: string;
  store_code: string;
  date: string;
  shift: string;
  accountable: string;
  itemStockout: string;
  itemDescription: string;
  correctiveAction: string;
  preventiveAction: string;
}

export interface SvrFormRow {
  id: string;
  store_code: string;
  date: string;
  shift: string;
  cashier: string;
  declaredCash: string;
  calculatedCash: string;
  netSpecialSales: string;
  cashOverShort: string;
  dateTransaction: string;
  cashDeposit: string;
  varianceDeclaredDeposited: string;
  otherDeposits: string;
  creditCardSales: string;
  creditCardCharge: string;
  crMemo: string;
  gc: string;
  paymaya: string;
  vipSold: string;
  vip: string;
  centuryShopaholicVouchers: string;
  gcash: string;
  foodPanda: string;
  honestBee: string;
  metrodealNet: string;
  marketing: string;
  other: string;
  lazada: string;
  shopee: string;
  grab: string;
  booky: string;
  poodtrip: string;
  pickaroo: string;
  parahero: string;
  rareFoodShop: string;
  shopeePay: string;
  metroMart: string;
  zalora: string;
  eatigo: string;
  madison: string;
  smOnline: string;
  arExe: string;
  robinsons: string;
  catering: string;
  snackshop: string;
  reseller: string;
  arOps: string;
  arsmLifestyle: string;
  arBellevuehotel: string;
  offsite: string;
  happyappWallet: string;
  hyperMart: string;
  leePlaza: string;
  uymatiaoShipping: string;
  allEasy: string;
  arAct: string;
  shareTreat: string;
  totalnonCash: string;
  totalPayments: string;
  bulkWholesale: string;
  othersSpecify: string;
  cateringManual: string;
  offsiteSelling: string;
  resellerManual: string;
  snackshopManual: string;
  deliveryfeeManual: string;
  cartSales: string;
  consignment: string;
  totalSpecialSales: string;
  overallSales: string;
  srCitizen: string;
  pwdDisc: string;
  otherDisc: string;
  transactionCountPos: string;
  transactionValue: string;
  gcOriginatingStore: string;
  gcSerialNumber: string;
  terminalId: string;
  voids: string;
}

// Define type for input events
type InputChangeEvent = React.ChangeEvent<
  HTMLInputElement | HTMLTextAreaElement
>;

// Define calendar event types
type EventType = "Daily" | "Weekly" | "Monthly";

// Calendar event interface
interface CalendarEvent {
  id: string;
  title: EventType;
  date: Date;
  color: string;
  recurring: boolean;
}

// Day interface for calendar
interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  events: EventType[];
}

// Steps for the stepper
const dailySteps = [
  "Attendance Form",
  "Incident Reports",
  "Inventory Levels",
  "Efficiency Metrics",
  "Svr",
];
const weeklySteps = ["Weekly Report 1"];
const monthlySteps = ["Monthly Report 1"];

// Day names for calendar header
const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function FormList(): React.ReactElement {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const attendanceStaticDataState = useAppSelector(selectDailyStaticData); // Add this line
  const [activeStep, setActiveStep] = useState<number>(0);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [showForms, setShowForms] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [dateWasSelected, setDateWasSelected] = useState<boolean>(false); // checks if a date is selected
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedEvent, setSelectedEvent] = useState<EventType | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [incidentValidation, setIncidentValidation] = useState<
    Record<string, boolean>
  >({}); // incident validation
  const [inventoryValidation, setInventoryValidation] = useState<
    Record<string, boolean>
  >({}); // inventory validation
  const [efficiencyValidation, setEfficiencyValidation] = useState<
    Record<string, boolean>
  >({}); // efficienct validations
  const [errorMessage, setErrorMessage] = useState<string | null>(null); // Existing error state
  const [dateSelectionAlert, setDateSelectionAlert] = useState<string | null>(
    null
  ); // New state for date selection alert
  const [viewRecordsDialogOpen, setViewRecordsDialogOpen] =
    useState<boolean>(false);
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [alertTitle, setAlertTitle] = React.useState("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Add these new state variables after existing ones
  const [todayStats, setTodayStats] = useState({
    totalEvents: 0,
    completedForms: 0,
    pendingForms: 0,
  });

  const [monthStats, setMonthStats] = useState({
    dailyEvents: 0,
    weeklyEvents: 0,
    monthlyEvents: 0,
  });

  // Generate a unique ID for new rows
  const generateUniqueId = () =>
    `row_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const { attendance_status_data, attendance_shift_data, employee_data } =
    attendanceStaticDataState.data?.attendanceData ?? {
      attendance_status_data: [],
      attendance_shift_data: [],
      employee_data: [],
    };

  // Attendance form rows state
  const [attendanceRows, setAttendanceRows] = useState<AttendanceFormRow[]>([
    {
      id: generateUniqueId(),
      store_code: "",
      attendance_date: "",
      shift: "",
      employee_name: "",
      attendance: "",
    },
  ]);

  useEffect(() => {
    if (!showForms) {
      setAttendanceRows([
        {
          id: generateUniqueId(),
          store_code: "",
          attendance_date: "",
          shift: "",
          employee_name: "",
          attendance: "",
        },
      ]);
    }
  }, [showForms]); //reset attendance rows on close

  // useEffect(() => {
  //   const getStoreCount = (adminSession: AdminSessionModel) => {
  //     const storeCount = adminSession.admin.user_details.stores.length;
  //     console.log("Number of store_ids:", storeCount);
  //   };

  //   // Example of fetching the data
  // }, []); // Empty dependency array for mount only

  useEffect(() => {
    if (attendanceStaticDataState.data) {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;

      // Reset attendanceRows with the new date and employee data
      const fillEmp = employee_data.map((emp) => ({
        id: generateUniqueId(),
        store_code: "",
        attendance_date: formattedDate,
        shift: "",
        employee_name: emp.id.toString(),
        attendance: "",
      }));

      setAttendanceRows(fillEmp);
    }
  }, [attendanceStaticDataState.data, selectedDate, employee_data, dispatch]);

  // Incident form rows state
  const [incidentRows, setIncidentRows] = useState<IncidentFormRow[]>([
    {
      id: generateUniqueId(),
      store_code: "",
      dateTimeReported: "",
      reportedTo: "",
      dateTimeOfIncident: "",
      reportedBy: "",
      peopleInvolved: "",
      actionTaken: "",
      remarks: "",
      status: "",
      employee_name: "",
    },
  ]);

  useEffect(() => {
    if (attendanceStaticDataState.data) {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;

      // Update all rows with the new date
      const updatedRows = incidentRows.map((row) => ({
        ...row,
        dateTimeReported: formattedDate,
      }));

      setIncidentRows(updatedRows);
    }
  }, [attendanceStaticDataState.data, selectedDate]);

  const [inventoryRows, setInventoryRows] = useState<InventoryFormRow[]>([
    {
      id: generateUniqueId(),
      store_code: "",
      date: "",
      itemDescription: "",
      criticalLevel: "",
      quantity: "",
      expiryDate: "",
    },
  ]);

  useEffect(() => {
    if (attendanceStaticDataState.data) {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;

      // Update all rows with the new date
      const updatedRows = inventoryRows.map((row) => ({
        ...row,
        date: formattedDate,
      }));

      setInventoryRows(updatedRows);
    }
  }, [attendanceStaticDataState.data, selectedDate]);

  const [efficiencyRows, setEfficiencyRows] = useState<EfficiencyFormRow[]>([
    {
      id: generateUniqueId(),
      store_code: "",
      date: "",
      shift: "",
      accountable: "",
      itemStockout: "",
      itemDescription: "",
      correctiveAction: "",
      preventiveAction: "",
    },
  ]);

  useEffect(() => {
    dispatch(dailyProduct());
  }, [dispatch]);

  useEffect(() => {
    if (attendanceStaticDataState.data) {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;

      // Update all rows with the new date
      const updatedRows = efficiencyRows.map((row) => ({
        ...row,
        date: formattedDate,
      }));

      setEfficiencyRows(updatedRows);
    }
  }, [attendanceStaticDataState.data, selectedDate]);

  const [svrRows, setSvrRows] = useState<SvrFormRow[]>([
    {
      id: generateUniqueId(),
      store_code: "",
      date: "",
      shift: "",
      cashier: "",
      declaredCash: "",
      calculatedCash: "",
      netSpecialSales: "",
      cashOverShort: "",
      dateTransaction: "",
      cashDeposit: "",
      varianceDeclaredDeposited: "",
      otherDeposits: "",
      creditCardSales: "",
      creditCardCharge: "",
      crMemo: "",
      gc: "",
      paymaya: "",
      vipSold: "",
      vip: "",
      centuryShopaholicVouchers: "",
      gcash: "",
      foodPanda: "",
      honestBee: "",
      metrodealNet: "",
      marketing: "",
      other: "",
      lazada: "",
      shopee: "",
      grab: "",
      booky: "",
      poodtrip: "",
      pickaroo: "",
      parahero: "",
      rareFoodShop: "",
      shopeePay: "",
      metroMart: "",
      zalora: "",
      eatigo: "",
      madison: "",
      smOnline: "",
      arExe: "",
      robinsons: "",
      catering: "",
      snackshop: "",
      reseller: "",
      arOps: "",
      arsmLifestyle: "",
      arBellevuehotel: "",
      offsite: "",
      happyappWallet: "",
      hyperMart: "",
      leePlaza: "",
      uymatiaoShipping: "",
      allEasy: "",
      arAct: "",
      shareTreat: "",
      totalnonCash: "",
      totalPayments: "",
      bulkWholesale: "",
      othersSpecify: "",
      cateringManual: "",
      offsiteSelling: "",
      resellerManual: "",
      snackshopManual: "",
      deliveryfeeManual: "",
      cartSales: "",
      consignment: "",
      totalSpecialSales: "",
      overallSales: "",
      srCitizen: "",
      pwdDisc: "",
      otherDisc: "",
      transactionCountPos: "",
      transactionValue: "",
      gcOriginatingStore: "",
      gcSerialNumber: "",
      terminalId: "",
      voids: "",
    },
  ]);

  useEffect(() => {
    if (attendanceStaticDataState.data) {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;

      // Update all rows with the new date
      const updatedRows = svrRows.map((row) => ({
        ...row,
        date: formattedDate,
      }));

      setSvrRows(updatedRows);
    }
  }, [attendanceStaticDataState.data, selectedDate]);

  // Add this useEffect to calculate statistics
  useEffect(() => {
    const today = new Date();
    const currentMonthEvents = getDaysForMonthView().filter(
      (day) => day.isCurrentMonth && day.events.length > 0
    );

    const dailyCount = currentMonthEvents.filter((day) =>
      day.events.includes("Daily")
    ).length;

    const weeklyCount = currentMonthEvents.filter((day) =>
      day.events.includes("Weekly")
    ).length;

    const monthlyCount = currentMonthEvents.filter((day) =>
      day.events.includes("Monthly")
    ).length;

    setMonthStats({
      dailyEvents: dailyCount,
      weeklyEvents: weeklyCount,
      monthlyEvents: monthlyCount,
    });

    // Today's events
    const todayEvents = getEventsForDate(today);
    setTodayStats({
      totalEvents: todayEvents.length,
      completedForms: 0, // You can track this based on your submission logic
      pendingForms: todayEvents.length,
    });
  }, [currentMonth]);

  // Initialize the transfer list states
  const [leftItems, setLeftItems] = useState<string[]>([
    "netSpecialSales",
    // "cashOverShort", -auto calculate
    // "dateTransaction",
    "cashDeposit",
    // "varianceDeclaredDeposited", -auto calculate
    "otherDeposits",
    "creditCardSales",
    "creditCardCharge",
    "crMemo",
    "gc",
    "paymaya",
    "vipSold",
    "vip",
    "centuryShopaholicVouchers",
    "gcash",
    "foodPanda",
    "honestBee",
    "metrodealNet",
    "marketing",
    "other",
    "lazada",
    "shopee",
    "grab",
    "booky",
    "poodtrip",
    "pickaroo",
    "parahero",
    "rareFoodShop",
    "shopeePay",
    "metroMart",
    "zalora",
    "eatigo",
    "madison",
    "smOnline",
    "arExe",
    "robinsons",
    "catering",
    "snackshop",
    "reseller",
    "arOps",
    "arsmLifestyle",
    "arBellevuehotel",
    "offsite",
    "happyappWallet",
    "hyperMart",
    "leePlaza",
    "uymatiaoShipping",
    "allEasy",
    "arAct",
    "shareTreat",
    "totalnonCash",
    // "totalPayments", -auto calculate
    "bulkWholesale",
    "othersSpecify",
    "cateringManual",
    "offsiteSelling",
    "resellerManual",
    "snackshopManual",
    "deliveryfeeManual",
    "cartSales",
    "consignment",
    "totalSpecialSales",
    // "overallSales", -auto calculate
    "srCitizen",
    "pwdDisc",
    "otherDisc",
    "transactionCountPos",
    // "transactionValue", -auto calculate
    "gcOriginatingStore",
    "gcSerialNumber",
    "terminalId",
    "voids",
  ]);

  const [rightItems, setRightItems] = useState<string[]>([]);

  // Handler for the transfer list changes
  const handleTransferListChange = (newLeft: string[], newRight: string[]) => {
    setLeftItems(newLeft);
    setRightItems(newRight);
  };

  // Calendar events
  const [events, setEvents] = useState<CalendarEvent[]>([
    {
      id: generateUniqueId(),
      title: "Daily",
      date: new Date(),
      color: "#800000",
      recurring: true,
    },
    {
      id: generateUniqueId(),
      title: "Weekly",
      date: new Date(),
      color: "#0F9D58",
      recurring: true,
    },
    {
      id: generateUniqueId(),
      title: "Monthly",
      date: new Date(),
      color: "#DC0000",
      recurring: true,
    },
  ]);

  // Get current steps based on selected event type
  const getCurrentSteps = (): string[] => {
    switch (selectedEvent) {
      case "Daily":
        return dailySteps;
      case "Weekly":
        return weeklySteps;
      case "Monthly":
        return monthlySteps;
      default:
        return dailySteps;
    }
  };

  // Get days in month
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get days for current month view
  const getDaysForMonthView = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = new Date(year, month, 1).getDay();

    const days: CalendarDay[] = [];

    // Previous month days to fill first week
    const prevMonthDays = new Date(year, month, 0).getDate();
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthDays - i);
      days.push({
        date,
        isCurrentMonth: false,
        events: getEventsForDate(date),
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      days.push({
        date,
        isCurrentMonth: true,
        events: getEventsForDate(date),
      });
    }

    // Next month days to fill last week
    const remainingDays = 42 - days.length; // 6 rows of 7 days
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(year, month + 1, i);
      days.push({
        date,
        isCurrentMonth: false,
        events: getEventsForDate(date),
      });
    }

    return days;
  };

  // Get events for a specific date
  const getEventsForDate = (date: Date): EventType[] => {
    const events: EventType[] = [];

    // Check for daily events (except Sundays but now including sunday)

    events.push("Daily");

    // Check for weekly events (every Monday)
    if (date.getDay() === 1) {
      events.push("Weekly");
    }

    // Check for monthly events (first Monday of the month)
    if (date.getDay() === 1 && date.getDate() <= 7) {
      events.push("Monthly");
    }

    return events;
  };

  // Handle date selection, when a user selects a date
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setDateWasSelected(true);
  };

  // Handle month navigation
  const goToPreviousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  const goToNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  // Handle event selection
  const handleEventSelect = (event: EventType) => {
    if (!dateWasSelected) {
      setDateSelectionAlert("Please Select a Day First");
      setAlertOpen(true); // Open the alert dialog
      setAlertTitle("Date Selection Required");
      setAlertMessage("Please select a date first.");
      return;
    }

    setDateSelectionAlert(null); // Clear any existing alert
    setIsLoading(true); // Start loading

    // Simulate loading delay (remove this setTimeout in production and rely on actual data fetching)
    setTimeout(() => {
      setSelectedEvent(event);
      setShowForms(true);
      setActiveStep(0);

      if (event === "Daily") {
        dispatch(dailyStaticData());
      }
      setIsLoading(false); // End loading
    }, 500); // 500ms delay for demonstration
  };

  // Handle attendance form input change
  const handleAttendanceInputChange = (
    id: string,
    e: InputChangeEvent
  ): void => {
    const { name, value } = e.target;
    if (name) {
      setAttendanceRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [name]: value } : row))
      );
    }
  };

  // Handle attendance form select change
  const handleAttendanceSelectChange = (
    id: string,
    e: SelectChangeEvent<any>
  ): void => {
    const { name, value } = e.target;
    if (name) {
      // First update the current field
      setAttendanceRows((prev) =>
        prev.map((row) => {
          if (row.id === id) {
            const updatedRow = { ...row, [name]: value };

            // If the field being changed is "attendance" and the value is between 4-16
            if (
              name === "attendance" &&
              parseInt(value) >= 4 &&
              parseInt(value) <= 16
            ) {
              // Set shift to "N/A" (ID 3)
              updatedRow.shift = "3";
            }

            return updatedRow;
          }
          return row;
        })
      );
    }
  };

  // Handle incident form input change
  const handleIncidentInputChange = (id: string, e: InputChangeEvent): void => {
    const { name, value } = e.target;
    if (name) {
      setIncidentRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [name]: value } : row))
      );
    }
  };

  // Handle incident form select change
  const handleIncidentSelectChange = (
    id: string,
    e: SelectChangeEvent<any>
  ): void => {
    const { name, value } = e.target;
    if (name) {
      setIncidentRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [name]: value } : row))
      );
    }
  };

  // Handle incident inventory input change
  const handleInventoryInputChange = (
    id: string,
    e: InputChangeEvent
  ): void => {
    const { name, value } = e.target;
    if (name) {
      setInventoryRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [name]: value } : row))
      );
    }
  };

  // Handle incident form select change
  const handleInventorySelectChange = (
    id: string,
    e: SelectChangeEvent<any>
  ): void => {
    const { name, value } = e.target;
    if (name) {
      setInventoryRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [name]: value } : row))
      );
    }
  };

  // Handle efficiency input change
  const handleEfficiencyInputChange = (
    id: string,
    e: InputChangeEvent
  ): void => {
    const { name, value } = e.target;
    if (name) {
      setEfficiencyRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [name]: value } : row))
      );
    }
  };

  // Handle efficiency select change
  const handleEfficiencySelectChange = (
    id: string,
    e: SelectChangeEvent<any>
  ): void => {
    const { name, value } = e.target;
    if (name) {
      setEfficiencyRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [name]: value } : row))
      );
    }
  };

  // Handle svr input change
  const handleSvrInputChange = (id: string, e: InputChangeEvent): void => {
    const { name, value } = e.target;
    if (name) {
      setSvrRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [name]: value } : row))
      );
    }
  };

  // Handle svr select change
  const handleSvrSelectChange = (
    id: string,
    e: SelectChangeEvent<any>
  ): void => {
    const { name, value } = e.target;
    if (name) {
      setSvrRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [name]: value } : row))
      );
    }
  };

  // Add new attendance row
  const addAttendanceRow = (): void => {
    setAttendanceRows((prev) => [
      ...prev,
      {
        id: generateUniqueId(),
        store_code: "",
        attendance_date: "",
        shift: "defaultShift",
        employee_name: "",
        attendance: "defaultAttendance",
      },
    ]);
  };

  // Add new incident row
  const addIncidentRow = (): void => {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDate.getDate()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day}`;
    setIncidentRows((prev) => [
      ...prev,
      {
        id: generateUniqueId(),
        store_code: "",
        dateTimeReported: formattedDate,
        reportedTo: "",
        dateTimeOfIncident: "",
        reportedBy: "",
        peopleInvolved: "",
        actionTaken: "",
        remarks: "",
        status: "",
        employee_name: "",
      },
    ]);
  };

  // Add new inventory row
  const addInventoryRow = (): void => {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDate.getDate()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day}`;
    setInventoryRows((prev) => [
      ...prev,
      {
        id: generateUniqueId(),
        store_code: "",
        date: formattedDate,
        itemDescription: "",
        criticalLevel: "",
        quantity: "",
        expiryDate: "",
      },
    ]);
  };

  // Add new efficiency row
  const addEfficiencyRow = (): void => {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDate.getDate()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day}`;
    setEfficiencyRows((prev) => [
      ...prev,
      {
        id: generateUniqueId(),
        store_code: "",
        date: formattedDate,
        shift: "",
        accountable: "",
        itemStockout: "",
        itemDescription: "",
        correctiveAction: "",
        preventiveAction: "",
      },
    ]);
  };

  // Add new svr row
  const addSvrRow = (): void => {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDate.getDate()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day}`;

    const newRow: SvrFormRow = {
      id: generateUniqueId(),
      store_code: "",
      date: formattedDate,
      shift: "",
      cashier: "",
      declaredCash: "",
      calculatedCash: "",
      netSpecialSales: "",
      cashOverShort: "",
      dateTransaction: "",
      cashDeposit: "",
      varianceDeclaredDeposited: "",
      otherDeposits: "",
      creditCardSales: "",
      creditCardCharge: "",
      crMemo: "",
      gc: "",
      paymaya: "",
      vipSold: "",
      vip: "",
      centuryShopaholicVouchers: "",
      gcash: "",
      foodPanda: "",
      honestBee: "",
      metrodealNet: "",
      marketing: "",
      other: "",
      lazada: "",
      shopee: "",
      grab: "",
      booky: "",
      poodtrip: "",
      pickaroo: "",
      parahero: "",
      rareFoodShop: "",
      shopeePay: "",
      metroMart: "",
      zalora: "",
      eatigo: "",
      madison: "",
      smOnline: "",
      arExe: "",
      robinsons: "",
      catering: "",
      snackshop: "",
      reseller: "",
      arOps: "",
      arsmLifestyle: "",
      arBellevuehotel: "",
      offsite: "",
      happyappWallet: "",
      hyperMart: "",
      leePlaza: "",
      uymatiaoShipping: "",
      allEasy: "",
      arAct: "",
      shareTreat: "",
      totalnonCash: "",
      totalPayments: "",
      bulkWholesale: "",
      othersSpecify: "",
      cateringManual: "",
      offsiteSelling: "",
      resellerManual: "",
      snackshopManual: "",
      deliveryfeeManual: "",
      cartSales: "",
      consignment: "",
      totalSpecialSales: "",
      overallSales: "",
      srCitizen: "",
      pwdDisc: "",
      otherDisc: "",
      transactionCountPos: "",
      transactionValue: "",
      gcOriginatingStore: "",
      gcSerialNumber: "",
      terminalId: "",
      voids: "",
    };

    setSvrRows((prev) => [...prev, newRow]);
  };

  // Remove attendance row
  const removeAttendanceRow = (id: string): void => {
    if (attendanceRows.length > 1) {
      setAttendanceRows((prev) => prev.filter((row) => row.id !== id));
    }
  };

  // Remove incident row
  const removeIncidentRow = (id: string): void => {
    if (incidentRows.length > 1) {
      setIncidentRows((prev) => prev.filter((row) => row.id !== id));
    }
  };

  // Remove inventory row
  const removeInventoryRow = (id: string): void => {
    if (inventoryRows.length > 1) {
      setInventoryRows((prev) => prev.filter((row) => row.id !== id));
    }
  };

  // Remove efficiency row
  const removeEfficiencyRow = (id: string): void => {
    if (efficiencyRows.length > 1) {
      setEfficiencyRows((prev) => prev.filter((row) => row.id !== id));
    }
  };

  // Remove svr row
  const removeSvrRow = (id: string): void => {
    if (svrRows.length > 1) {
      setSvrRows((prev) => prev.filter((row) => row.id !== id));
    }
  };

  // Open confirmation dialog
  const openConfirmationDialog = (): void => {
    setConfirmDialogOpen(true);
  };

  // Handle form submission after confirmation
  const handleConfirmedSubmit = (): void => {
    const hasInvalidAttendance = attendanceRows.some((row) => {
      return (
        !row.attendance_date ||
        !row.attendance ||
        !row.employee_name ||
        !row.shift
      );
    });

    if (hasInvalidAttendance) {
      setAlertTitle("Attendance Form Error");
      setAlertMessage("Please fill all required fields in the Attendance Form");
      setAlertOpen(true);
      return;
    }

    const hasInvalidSvr = svrRows.some((row) => {
      return (
        !row.date ||
        !row.dateTransaction ||
        !row.shift ||
        !row.cashier ||
        !row.declaredCash ||
        !row.calculatedCash
      );
    });

    if (hasInvalidSvr) {
      setAlertTitle("SVR Form Error");
      setAlertMessage("Please fill all required fields in the SVR Form");
      setAlertOpen(true);
      return;
    }

    const filteredIncidentData = incidentRows.filter((row) => {
      const requireAll = shouldRequireFields(row);
      if (row.dateTimeReported && !requireAll) return false;
      return (
        !requireAll ||
        (row.dateTimeReported &&
          row.dateTimeOfIncident &&
          row.reportedTo &&
          row.reportedBy &&
          row.peopleInvolved &&
          row.actionTaken &&
          row.status &&
          row.remarks)
      );
    });

    const filteredInventoryData = inventoryRows.filter((row) => {
      const requireAll = shouldRequireInventoryFields(row);
      if (row.date && !requireAll) return false;
      return (
        !requireAll ||
        (row.date &&
          row.itemDescription &&
          row.criticalLevel &&
          row.quantity &&
          row.expiryDate)
      );
    });

    const filteredEfficiencyData = efficiencyRows.filter((row) => {
      const requireAll = shouldRequireEfficiencyFields(row);
      if (row.date && !requireAll) return false;
      return (
        !requireAll ||
        (row.date &&
          row.shift &&
          row.accountable &&
          row.itemStockout &&
          row.itemDescription &&
          row.correctiveAction &&
          row.preventiveAction)
      );
    });

    const hasInvalidIncidents = incidentRows.some((row) => {
      const requireAll = shouldRequireFields(row);
      return (
        requireAll &&
        !(
          row.dateTimeReported &&
          row.dateTimeOfIncident &&
          row.reportedTo &&
          row.reportedBy &&
          row.peopleInvolved &&
          row.actionTaken &&
          row.status &&
          row.remarks
        )
      );
    });

    const hasInvalidInventory = inventoryRows.some((row) => {
      const requireAll = shouldRequireInventoryFields(row);
      return (
        requireAll &&
        !(
          row.date &&
          row.itemDescription &&
          row.criticalLevel &&
          row.quantity &&
          row.expiryDate
        )
      );
    });

    const hasInvalidEfficiency = efficiencyRows.some((row) => {
      const requireAll = shouldRequireEfficiencyFields(row);
      return (
        requireAll &&
        !(
          row.date &&
          row.shift &&
          row.accountable &&
          row.itemStockout &&
          row.itemDescription &&
          row.correctiveAction &&
          row.preventiveAction
        )
      );
    });

    if (hasInvalidIncidents) {
      setAlertTitle("Incident Reports Error");
      setAlertMessage(
        "Please fill all required fields in the Incident Reports"
      );
      setAlertOpen(true);
      return;
    }

    if (hasInvalidInventory) {
      setAlertTitle("Inventory Levels Error");
      setAlertMessage(
        "Please fill all required fields in the Inventory Levels"
      );
      setAlertOpen(true);
      return;
    }

    if (hasInvalidEfficiency) {
      setAlertTitle("Efficiency Metrics Error");
      setAlertMessage(
        "Please fill all required fields in the Efficiency Metrics"
      );
      setAlertOpen(true);
      return;
    }

    // Calculate SVR values before submission
    const calculatedSvrRows = svrRows.map((row) => {
      const declaredCashNum = parseFloat(row.declaredCash) || 0;
      const calculatedCashNum = parseFloat(row.calculatedCash) || 0;
      const cashDepositNum = parseFloat(row.cashDeposit) || 0;
      const totalnonCashNum = parseFloat(row.totalnonCash) || 0;
      const totalSpecialSalesNum = parseFloat(row.totalSpecialSales) || 0;
      const transactionCountPosNum = parseFloat(row.transactionCountPos) || 0;

      // 1. cashOverShort = declaredCash - calculatedCash
      const cashOverShort = (declaredCashNum - calculatedCashNum).toString();

      // 2. varianceDeclaredDeposited = cashDeposit - declaredCash
      const varianceDeclaredDeposited = (
        cashDepositNum - declaredCashNum
      ).toString();

      // 3. totalPayments = totalnonCash + calculatedCash
      const totalPayments = (totalnonCashNum + calculatedCashNum).toString();

      // 4. overallSales = totalSpecialSales + totalnonCash + calculatedCash
      const overallSales = (
        totalSpecialSalesNum +
        totalnonCashNum +
        calculatedCashNum
      ).toString();

      // 5. transactionValue = overallSales / transactionCountPos
      const transactionValue =
        transactionCountPosNum !== 0
          ? (parseFloat(overallSales) / transactionCountPosNum).toString()
          : "0";

      return {
        ...row,
        cashOverShort: row.cashOverShort || cashOverShort,
        varianceDeclaredDeposited:
          row.varianceDeclaredDeposited || varianceDeclaredDeposited,
        totalPayments: row.totalPayments || totalPayments,
        overallSales: row.overallSales || overallSales,
        transactionValue: row.transactionValue || transactionValue,
      };
    });

    setConfirmDialogOpen(false);
    dispatch(
      formList({
        attendanceData: attendanceRows,
        incidentData: filteredIncidentData,
        inventoryData: filteredInventoryData,
        efficiencyData: filteredEfficiencyData,
        svrData: calculatedSvrRows,
      })
    );

    console.log("Submitting attendance data:", attendanceRows);
    console.log("Submitting incident data:", incidentRows);
    console.log("Submitting inventory data:", inventoryRows);
    console.log("Submitting efficiency data:", efficiencyRows);
    console.log("Submitting svr data:", calculatedSvrRows);
    console.log("Event type:", selectedEvent);
    console.log("Selected date:", selectedDate);

    setAttendanceRows([
      {
        id: generateUniqueId(),
        store_code: "",
        attendance_date: "",
        shift: "AM",
        employee_name: "",
        attendance: "Present",
      },
    ]);

    setIncidentRows([
      {
        id: generateUniqueId(),
        store_code: "",
        dateTimeReported: "",
        reportedTo: "",
        dateTimeOfIncident: "",
        reportedBy: "",
        peopleInvolved: "",
        actionTaken: "",
        remarks: "",
        status: "",
        employee_name: "",
      },
    ]);

    setInventoryRows([
      {
        id: generateUniqueId(),
        store_code: "",
        date: "",
        itemDescription: "",
        criticalLevel: "",
        quantity: "",
        expiryDate: "",
      },
    ]);

    setSvrRows([
      {
        id: generateUniqueId(),
        store_code: "",
        date: "",
        shift: "",
        cashier: "",
        declaredCash: "",
        calculatedCash: "",
        netSpecialSales: "",
        cashOverShort: "",
        dateTransaction: "",
        cashDeposit: "",
        varianceDeclaredDeposited: "",
        otherDeposits: "",
        creditCardSales: "",
        creditCardCharge: "",
        crMemo: "",
        gc: "",
        paymaya: "",
        vipSold: "",
        vip: "",
        centuryShopaholicVouchers: "",
        gcash: "",
        foodPanda: "",
        honestBee: "",
        metrodealNet: "",
        marketing: "",
        other: "",
        lazada: "",
        shopee: "",
        grab: "",
        booky: "",
        poodtrip: "",
        pickaroo: "",
        parahero: "",
        rareFoodShop: "",
        shopeePay: "",
        metroMart: "",
        zalora: "",
        eatigo: "",
        madison: "",
        smOnline: "",
        arExe: "",
        robinsons: "",
        catering: "",
        snackshop: "",
        reseller: "",
        arOps: "",
        arsmLifestyle: "",
        arBellevuehotel: "",
        offsite: "",
        happyappWallet: "",
        hyperMart: "",
        leePlaza: "",
        uymatiaoShipping: "",
        allEasy: "",
        arAct: "",
        shareTreat: "",
        totalnonCash: "",
        totalPayments: "",
        bulkWholesale: "",
        othersSpecify: "",
        cateringManual: "",
        offsiteSelling: "",
        resellerManual: "",
        snackshopManual: "",
        deliveryfeeManual: "",
        cartSales: "",
        consignment: "",
        totalSpecialSales: "",
        overallSales: "",
        srCitizen: "",
        pwdDisc: "",
        otherDisc: "",
        transactionCountPos: "",
        transactionValue: "",
        gcOriginatingStore: "",
        gcSerialNumber: "",
        terminalId: "",
        voids: "",
      },
    ]);

    setSubmitted(true);
    setActiveStep(0);
    setShowForms(false);
    setSelectedEvent(null);
  };

  // Go to view page based on current case
  const goToViewPage = (): void => {
    // Open the dialog instead of using window.confirm
    setViewRecordsDialogOpen(true);
  };

  // Add a handler for confirming the navigation
  const handleViewRecordsConfirm = () => {
    const baseUrl = `${window.location.origin}${
      window.location.pathname.split("/admin")[0]
    }`;

    switch (activeStep) {
      case 0:
        window.open(`${baseUrl}/admin/ms6/manpower/daily-attendance`, "_blank");
        break;
      case 1:
        window.open(`${baseUrl}/admin/ms6/manpower/incident-report`, "_blank");
        break;
      case 2:
        window.open(`${baseUrl}/admin/ms6/material/inventory-levels`, "_blank");
        break;
      case 3:
        window.open(
          `${baseUrl}/admin/ms6/material/efficiency-metric`,
          "_blank"
        );
        break;
      case 4:
        window.open(`${baseUrl}/admin/ms6/money`, "_blank");
        break;
      default:
        window.open(`${baseUrl}/admin/ms6/manpower`, "_blank");
        break;
    }

    setViewRecordsDialogOpen(false);
  };

  // Handle next step
  const handleNext = (): void => {
    // Validation checks based on current step
    let hasErrors = false;
    let errorTitle = "";
    let errorMessage = "";

    switch (activeStep) {
      case 0: // Attendance Form
        const hasInvalidAttendance = attendanceRows.some((row) => {
          return (
            !row.attendance_date ||
            !row.attendance ||
            !row.employee_name ||
            !row.shift
          );
        });
        if (hasInvalidAttendance) {
          hasErrors = true;
          errorTitle = "Attendance Form Error";
          errorMessage =
            "Please fill all required fields in the Attendance Form";
        }
        break;

      case 1: // Incident Reports
        const hasInvalidIncidents = incidentRows.some((row) => {
          const requireAll = shouldRequireFields(row);
          return (
            requireAll &&
            !(
              row.dateTimeReported &&
              row.dateTimeOfIncident &&
              row.reportedTo &&
              row.reportedBy &&
              row.peopleInvolved &&
              row.actionTaken &&
              row.status &&
              row.remarks
            )
          );
        });
        if (hasInvalidIncidents) {
          hasErrors = true;
          errorTitle = "Incident Reports Error";
          errorMessage =
            "Please fill all required fields in the Incident Reports";
        }
        break;

      case 2: // Inventory Levels
        const hasInvalidInventory = inventoryRows.some((row) => {
          const requireAll = shouldRequireInventoryFields(row);
          return (
            requireAll &&
            !(
              row.date &&
              row.itemDescription &&
              row.criticalLevel &&
              row.quantity &&
              row.expiryDate
            )
          );
        });
        if (hasInvalidInventory) {
          hasErrors = true;
          errorTitle = "Inventory Levels Error";
          errorMessage =
            "Please fill all required fields in the Inventory Levels";
        }
        break;

      case 3: // Efficiency Metrics
        const hasInvalidEfficiency = efficiencyRows.some((row) => {
          const requireAll = shouldRequireEfficiencyFields(row);
          return (
            requireAll &&
            !(
              row.date &&
              row.shift &&
              row.accountable &&
              row.itemStockout &&
              row.itemDescription &&
              row.correctiveAction &&
              row.preventiveAction
            )
          );
        });
        if (hasInvalidEfficiency) {
          hasErrors = true;
          errorTitle = "Efficiency Metrics Error";
          errorMessage =
            "Please fill all required fields in the Efficiency Metrics";
        }
        break;

      case 4: // SVR Form
        const hasInvalidSvr = svrRows.some((row) => {
          return (
            !row.date ||
            !row.dateTransaction ||
            !row.shift ||
            !row.cashier ||
            !row.declaredCash ||
            !row.calculatedCash
          );
        });
        if (hasInvalidSvr) {
          hasErrors = true;
          errorTitle = "SVR Form Error";
          errorMessage = "Please fill all required fields in the SVR Form";
        }
        break;

      default:
        break;
    }

    if (hasErrors) {
      setAlertTitle(errorTitle);
      setAlertMessage(errorMessage);
      setAlertOpen(true);
      return;
    }

    // If no errors, proceed to next step
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  // Handle back step
  const handleBack = (): void => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Render confirmation dialog content
  const renderConfirmationDialogContent = (): React.ReactNode => {
    return (
      <>
        <DialogContentText>
          Please review the missing required fields before submitting:
        </DialogContentText>

        <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: "bold" }}>
          Event Type: {selectedEvent}
        </Typography>
        <Typography variant="body2">
          Date: {selectedDate.toLocaleDateString()}
        </Typography>

        {/* Form 0: Attendance Form */}
        {selectedEvent === "Daily" && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              Attendance Form
            </Typography>
            {attendanceRows.every(
              (row) =>
                row.attendance_date &&
                row.attendance &&
                row.employee_name &&
                row.shift
            ) ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "success.main",
                }}
              >
                <FcCheckmark style={{ marginRight: 8 }} />
                <Typography>All required fields completed</Typography>
              </Box>
            ) : (
              <List dense>
                {attendanceRows.map((row, index) => {
                  const missingFields = [];
                  if (!row.attendance_date) missingFields.push("Date");
                  if (!row.attendance) missingFields.push("Attendance");
                  if (!row.employee_name) missingFields.push("Employee Name");
                  if (!row.shift) missingFields.push("Shift");

                  return missingFields.length > 0 ? (
                    <ListItem key={row.id}>
                      <ListItemText
                        primary={`Record ${index + 1}`}
                        secondary={`Missing: ${missingFields.join(", ")}`}
                      />
                    </ListItem>
                  ) : null;
                })}
              </List>
            )}
          </Box>
        )}

        {/* Form 1: Incident Reports */}
        {selectedEvent === "Daily" && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              Incident Reports (Optional)
            </Typography>
            {incidentRows.every((row) => {
              const requireAll = shouldRequireFields(row);
              return (
                !requireAll ||
                (row.dateTimeReported &&
                  row.dateTimeOfIncident &&
                  row.reportedTo &&
                  row.reportedBy &&
                  row.peopleInvolved &&
                  row.actionTaken &&
                  row.status &&
                  row.remarks)
              );
            }) ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "success.main",
                }}
              >
                <FcCheckmark style={{ marginRight: 8 }} />
                <Typography>
                  All required fields completed or no incidents reported
                </Typography>
              </Box>
            ) : (
              <List dense>
                {incidentRows.map((row, index) => {
                  const requireAll = shouldRequireFields(row);
                  if (!requireAll) return null;

                  const missingFields = [];
                  if (!row.dateTimeReported)
                    missingFields.push("Date Reported");
                  if (!row.dateTimeOfIncident)
                    missingFields.push("Date of Incident");
                  if (!row.reportedTo) missingFields.push("Reported To");
                  if (!row.reportedBy) missingFields.push("Reported By");
                  if (!row.peopleInvolved)
                    missingFields.push("People Involved");
                  if (!row.actionTaken) missingFields.push("Action Taken");
                  if (!row.status) missingFields.push("Status");
                  if (!row.remarks) missingFields.push("Remarks");

                  return missingFields.length > 0 ? (
                    <ListItem key={row.id}>
                      <ListItemText
                        primary={`Incident ${index + 1}`}
                        secondary={`Missing: ${missingFields.join(", ")}`}
                      />
                    </ListItem>
                  ) : null;
                })}
              </List>
            )}
          </Box>
        )}

        {/* Form 2: Inventory Levels */}
        {selectedEvent === "Daily" && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              Inventory Levels (Optional)
            </Typography>
            {inventoryRows.every((row) => {
              const requireAll = shouldRequireInventoryFields(row);
              return (
                !requireAll ||
                (row.date &&
                  row.itemDescription &&
                  row.criticalLevel &&
                  row.quantity &&
                  row.expiryDate)
              );
            }) ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "success.main",
                }}
              >
                <FcCheckmark style={{ marginRight: 8 }} />
                <Typography>
                  All required fields completed or no inventory reported
                </Typography>
              </Box>
            ) : (
              <List dense>
                {inventoryRows.map((row, index) => {
                  const requireAll = shouldRequireInventoryFields(row);
                  if (!requireAll) return null;

                  const missingFields = [];
                  if (!row.date) missingFields.push("Date");
                  if (!row.itemDescription)
                    missingFields.push("Item Description");
                  if (!row.criticalLevel) missingFields.push("Critical Level");
                  if (!row.quantity) missingFields.push("Quantity");
                  if (!row.expiryDate) missingFields.push("Expiry Date");

                  return missingFields.length > 0 ? (
                    <ListItem key={row.id}>
                      <ListItemText
                        primary={`Inventory ${index + 1}`}
                        secondary={`Missing: ${missingFields.join(", ")}`}
                      />
                    </ListItem>
                  ) : null;
                })}
              </List>
            )}
          </Box>
        )}

        {/* Form 3: Efficiency Metrics */}
        {selectedEvent === "Daily" && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              Efficiency Metrics (Optional)
            </Typography>
            {efficiencyRows.every((row) => {
              const requireAll = shouldRequireEfficiencyFields(row);
              return (
                !requireAll ||
                (row.date &&
                  row.shift &&
                  row.accountable &&
                  row.itemStockout &&
                  row.itemDescription &&
                  row.correctiveAction &&
                  row.preventiveAction)
              );
            }) ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "success.main",
                }}
              >
                <FcCheckmark style={{ marginRight: 8 }} />
                <Typography>
                  All required fields completed or no efficiency metrics
                  reported
                </Typography>
              </Box>
            ) : (
              <List dense>
                {efficiencyRows.map((row, index) => {
                  const requireAll = shouldRequireEfficiencyFields(row);
                  if (!requireAll) return null;

                  const missingFields = [];
                  if (!row.date) missingFields.push("Date");
                  if (!row.shift) missingFields.push("Shift");
                  if (!row.accountable) missingFields.push("Accountable");
                  if (!row.itemStockout) missingFields.push("Item Stockout");
                  if (!row.itemDescription)
                    missingFields.push("Item Description");
                  if (!row.correctiveAction)
                    missingFields.push("Corrective Action");
                  if (!row.preventiveAction)
                    missingFields.push("Preventive Action");

                  return missingFields.length > 0 ? (
                    <ListItem key={row.id}>
                      <ListItemText
                        primary={`Efficiency ${index + 1}`}
                        secondary={`Missing: ${missingFields.join(", ")}`}
                      />
                    </ListItem>
                  ) : null;
                })}
              </List>
            )}
          </Box>
        )}

        {/* Form 4: SVR Form */}
        {selectedEvent === "Daily" && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              SVR Form
            </Typography>
            {svrRows.every(
              (row) =>
                row.date &&
                row.dateTransaction && // Add this check
                row.shift &&
                row.cashier &&
                row.declaredCash &&
                row.calculatedCash
            ) ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "success.main",
                }}
              >
                <FcCheckmark style={{ marginRight: 8 }} />
                <Typography>All required fields completed</Typography>
              </Box>
            ) : (
              <List dense>
                {svrRows.map((row, index) => {
                  const missingFields = [];
                  if (!row.date) missingFields.push("Date");
                  if (!row.dateTransaction)
                    missingFields.push("Date of Transaction"); // Add this
                  if (!row.shift) missingFields.push("Shift");
                  if (!row.cashier) missingFields.push("Cashier");
                  if (!row.declaredCash) missingFields.push("Declared Cash");
                  if (!row.calculatedCash)
                    missingFields.push("Calculated Cash");
                  return missingFields.length > 0 ? (
                    <ListItem key={row.id}>
                      <ListItemText
                        primary={`SVR ${index + 1}`}
                        secondary={`Missing: ${missingFields.join(", ")}`}
                      />
                    </ListItem>
                  ) : null;
                })}
              </List>
            )}
          </Box>
        )}

        <DialogContentText sx={{ mt: 2 }}>
          {attendanceRows.every(
            (row) =>
              row.attendance_date &&
              row.attendance &&
              row.employee_name &&
              row.shift
          ) &&
          svrRows.every(
            (row) =>
              row.date &&
              row.dateTransaction &&
              row.shift &&
              row.cashier &&
              row.declaredCash &&
              row.calculatedCash
          )
            ? "All mandatory forms are complete. Would you like to submit?"
            : "Please fill in all required fields before submitting."}
        </DialogContentText>
      </>
    );
  };

  // Get content for current step based on event type
  const getStepContent = (step: number): React.ReactNode => {
    // Render content based on event type
    switch (selectedEvent) {
      case "Daily":
        return getDailyStepContent(step);
      case "Weekly":
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Weekly reports will be available in the future.
            </Typography>
            <Typography variant="body1">
              This feature is currently under development. Please check back
              later.
            </Typography>
          </Box>
        );
      case "Monthly":
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Monthly reports will be available in the future.
            </Typography>
            <Typography variant="body1">
              This feature is currently under development. Please check back
              later.
            </Typography>
          </Box>
        );
      default:
        return "Unknown step";
    }
  };

  // Add this helper function above getDailyStepContent
  const shouldRequireFields = (row: IncidentFormRow) => {
    // Check if any field except dateTimeReported has a value
    return (
      row.reportedTo !== "" ||
      row.dateTimeOfIncident !== "" ||
      row.reportedBy !== "" ||
      row.peopleInvolved !== "" ||
      row.actionTaken !== "" ||
      row.remarks !== "" ||
      row.status !== "" ||
      row.employee_name !== ""
    );
  };

  // Add this helper function above getDailyStepContent
  const shouldRequireInventoryFields = (row: InventoryFormRow) => {
    // Check if any field except date has a value
    return (
      row.itemDescription !== "" ||
      row.criticalLevel !== "" ||
      row.quantity !== "" ||
      row.expiryDate !== ""
    );
  };

  // should require for efficiency fields
  const shouldRequireEfficiencyFields = (row: EfficiencyFormRow) => {
    // Check if any field except date has a value
    return (
      row.shift !== "" ||
      row.accountable !== "" ||
      row.itemStockout !== "" ||
      row.itemDescription !== "" ||
      row.correctiveAction !== "" ||
      row.preventiveAction !== ""
    );
  };

  // Get content for daily event steps
  const getDailyStepContent = (step: number): React.ReactNode => {
    switch (step) {
      case 0:
        // Add this new function before the return statement
        const clearAttendanceFields = () => {
          setAttendanceRows([
            {
              id: generateUniqueId(),
              store_code: "",
              attendance_date: "",
              shift: "",
              employee_name: "",
              attendance: "",
            },
          ]);
        };

        return (
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: "'Bebas Neue', sans-serif", // Change font to Bebas Neue
                fontSize: "1.5rem", // Set font size to 1.5rem (same as "Daily Event - 3/27/2025")
                color: "black", // Match the color of other headings
              }}
            >
              Attendance Form (Daily)
            </Typography>

            {attendanceRows.map((row, index) => {
              // Check if selected attendance status is in range 4-16 (rest day or any leave type)
              const isLeaveOrRestDay = row.attendance
                ? parseInt(row.attendance) >= 4 &&
                  parseInt(row.attendance) <= 16
                : false;

              return (
                <Paper key={row.id} elevation={1} sx={{ mb: 2, p: 2 }}>
                  <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        label="Date"
                        name="attendance_date"
                        type="date"
                        required
                        value={row.attendance_date}
                        onChange={(e) => handleAttendanceInputChange(row.id, e)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        size="small"
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <FormControl
                        fullWidth
                        size="small"
                        required
                        error={!row.attendance}
                      >
                        <InputLabel>Attendance</InputLabel>
                        <Select
                          name="attendance"
                          value={row.attendance}
                          label="Attendance"
                          onChange={(e) =>
                            handleAttendanceSelectChange(row.id, e)
                          }
                          required
                        >
                          {attendance_status_data.map((status) => (
                            <MenuItem
                              key={status.id}
                              value={status.id.toString()}
                            >
                              {status.name || status.id || status.id.toString()}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small" disabled>
                        <InputLabel>Employee Name</InputLabel>
                        <Select
                          name="employee_name"
                          value={row.employee_name}
                          label="Employee Name"
                          inputProps={{ readOnly: true }}
                          required
                        >
                          {employee_data.map((emp) => (
                            <MenuItem key={emp.id} value={emp.id.toString()}>
                              {emp.name || emp.id.toString()}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <FormControl
                        fullWidth
                        size="small"
                        disabled={isLeaveOrRestDay}
                        required
                        error={!row.shift}
                      >
                        <InputLabel>Shift</InputLabel>
                        <Select
                          name="shift"
                          required
                          value={isLeaveOrRestDay ? "3" : row.shift}
                          label="Shift"
                          onChange={(e) =>
                            handleAttendanceSelectChange(row.id, e)
                          }
                          inputProps={{ readOnly: isLeaveOrRestDay }}
                        >
                          {attendance_shift_data.map((shift) => (
                            <MenuItem
                              key={shift.id}
                              value={shift.id.toString()}
                            >
                              {shift.name || shift.id || shift.id.toString()}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {/* <Grid item xs={12} sm={1}>
                      <IconButton
                        color="error"
                        onClick={() => removeAttendanceRow(row.id)}
                        disabled={attendanceRows.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid> */}
                  </Grid>
                </Paper>
              );
            })}

            {/* <Button
              startIcon={<AddCircleOutlineIcon />}
              onClick={addAttendanceRow}
              variant="outlined"
              sx={{ mt: 1 }}
            >
              Add Row
            </Button> */}
          </Box>
        );

      case 1:
        const clearIncidentFields = () => {
          setIncidentRows([
            {
              id: generateUniqueId(),
              store_code: "",
              dateTimeReported: "",
              reportedTo: "",
              dateTimeOfIncident: "",
              reportedBy: "",
              peopleInvolved: "",
              actionTaken: "",
              remarks: "",
              status: "",
              employee_name: "",
            },
          ]);
        };

        return (
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: "'Bebas Neue', sans-serif", // Change font to Bebas Neue
                fontSize: "1.5rem", // Set font size to 1.5rem (same as "Daily Event - 3/27/2025")
                color: "black", // Match the color of other headings
              }}
            >
              Incident Reports (Optional)
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Note: If you enter any field, all fields become required. Leave
              all blank if no incident occurred.
            </Typography>

            {incidentRows.map((row, index) => {
              const requireAllFields = shouldRequireFields(row);

              return (
                <Paper key={row.id} elevation={1} sx={{ mb: 4, p: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Incident #{index + 1}
                  </Typography>

                  {/* Basic Information */}
                  <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Date Reported"
                        name="dateTimeReported"
                        type="date"
                        required
                        value={row.dateTimeReported}
                        onChange={(e) => handleIncidentInputChange(row.id, e)}
                        disabled
                        InputLabelProps={{
                          shrink: true,
                        }}
                        size="small"
                        error={requireAllFields && !row.dateTimeReported}
                        helperText={
                          requireAllFields && !row.dateTimeReported
                            ? "Required"
                            : ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Date of Incident"
                        name="dateTimeOfIncident"
                        type="datetime-local"
                        required={requireAllFields}
                        value={row.dateTimeOfIncident}
                        onChange={(e) => handleIncidentInputChange(row.id, e)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        size="small"
                        error={requireAllFields && !row.dateTimeOfIncident}
                        helperText={
                          requireAllFields && !row.dateTimeOfIncident
                            ? "Required"
                            : ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl
                        fullWidth
                        size="small"
                        required={requireAllFields}
                        error={requireAllFields && !row.reportedTo}
                      >
                        <InputLabel>Reported To</InputLabel>
                        <Select
                          name="reportedTo"
                          value={row.reportedTo}
                          label="Reported To"
                          onChange={(e) =>
                            handleIncidentSelectChange(row.id, e)
                          }
                        >
                          {employee_data.map((emp) => (
                            <MenuItem key={emp.id} value={emp.id.toString()}>
                              {emp.name || emp.id.toString()}
                            </MenuItem>
                          ))}
                        </Select>
                        {requireAllFields && !row.reportedTo && (
                          <Typography color="error" variant="caption">
                            Required
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl
                        fullWidth
                        size="small"
                        required={requireAllFields}
                        error={requireAllFields && !row.reportedBy}
                      >
                        <InputLabel>Reported By</InputLabel>
                        <Select
                          name="reportedBy"
                          value={row.reportedBy}
                          label="Reported By"
                          onChange={(e) =>
                            handleIncidentSelectChange(row.id, e)
                          }
                        >
                          {employee_data.map((emp) => (
                            <MenuItem key={emp.id} value={emp.id.toString()}>
                              {emp.name || emp.id.toString()}
                            </MenuItem>
                          ))}
                        </Select>
                        {requireAllFields && !row.reportedBy && (
                          <Typography color="error" variant="caption">
                            Required
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  {/* People and Actions */}
                  <Typography variant="subtitle1" gutterBottom>
                    People and Actions
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                      <FormControl
                        fullWidth
                        size="small"
                        required={requireAllFields}
                        error={requireAllFields && !row.peopleInvolved}
                      >
                        <InputLabel>People Involved</InputLabel>
                        <Select
                          name="peopleInvolved"
                          multiple
                          value={
                            Array.isArray(row.peopleInvolved)
                              ? row.peopleInvolved
                              : []
                          }
                          label="People Involved"
                          onChange={(e) =>
                            handleIncidentSelectChange(row.id, e)
                          }
                          renderValue={(selected: string[]) =>
                            selected
                              .map((id: string) => {
                                const employee = employee_data.find(
                                  (emp) => emp.id.toString() === id
                                );
                                return employee?.name || id;
                              })
                              .join(", ")
                          }
                        >
                          {employee_data.map((emp) => (
                            <MenuItem key={emp.id} value={emp.id.toString()}>
                              <Checkbox
                                checked={
                                  Array.isArray(row.peopleInvolved)
                                    ? row.peopleInvolved.includes(
                                        emp.id.toString()
                                      )
                                    : false
                                }
                              />
                              <ListItemText
                                primary={emp.name || emp.id.toString()}
                              />
                            </MenuItem>
                          ))}
                        </Select>
                        {requireAllFields && !row.peopleInvolved && (
                          <Typography color="error" variant="caption">
                            Required
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl
                        fullWidth
                        size="small"
                        required={requireAllFields}
                        error={requireAllFields && !row.status}
                      >
                        <InputLabel>Status</InputLabel>
                        <Select
                          name="status"
                          value={row.status}
                          label="Status"
                          onChange={(e) =>
                            handleIncidentSelectChange(row.id, e)
                          }
                        >
                          <MenuItem value="Done">Done</MenuItem>
                          <MenuItem value="Pending">Pending</MenuItem>
                        </Select>
                        {requireAllFields && !row.status && (
                          <Typography color="error" variant="caption">
                            Required
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Action Taken"
                        name="actionTaken"
                        required={requireAllFields}
                        value={row.actionTaken}
                        onChange={(e) => handleIncidentInputChange(row.id, e)}
                        size="small"
                        error={requireAllFields && !row.actionTaken}
                        helperText={
                          requireAllFields && !row.actionTaken ? "Required" : ""
                        }
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  {/* Remarks */}
                  <Typography variant="subtitle1" gutterBottom>
                    Remarks
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextareaAutosize
                        aria-label="remarks"
                        name="remarks"
                        minRows={3}
                        placeholder="Enter remarks"
                        value={row.remarks}
                        onChange={(e) => handleIncidentInputChange(row.id, e)}
                        style={{
                          width: "100%",
                          padding: "8px",
                          fontFamily: "inherit",
                          fontSize: "0.875rem",
                          borderRadius: "4px",
                          borderColor:
                            requireAllFields && !row.remarks
                              ? "#f44336"
                              : "rgba(0, 0, 0, 0.23)",
                        }}
                        required={requireAllFields}
                      />
                      {requireAllFields && !row.remarks && (
                        <Typography color="error" variant="caption">
                          Required
                        </Typography>
                      )}
                    </Grid>
                  </Grid>

                  <Box
                    sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}
                  >
                    <IconButton
                      color="error"
                      onClick={() => removeIncidentRow(row.id)}
                      disabled={incidentRows.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Paper>
              );
            })}
            <Box sx={{ display: "flex", gap: 2, mt: 2, mb: 2 }}>
              <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={addIncidentRow}
                variant="outlined"
                color="primary"
              >
                ADD INCIDENT
              </Button>
              <Button
                startIcon={<RemoveCircleOutlineIcon />}
                variant="outlined"
                color="secondary"
                onClick={clearIncidentFields}
              >
                CLEAR FIELDS
              </Button>
            </Box>
          </Box>
        );

      case 2:
        // Add this new function
        const clearInventoryFields = () => {
          setInventoryRows([
            {
              id: generateUniqueId(),
              store_code: "",
              date: "",
              itemDescription: "",
              criticalLevel: "",
              quantity: "",
              expiryDate: "",
            },
          ]);
        };

        return (
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: "'Bebas Neue', sans-serif", // Change font to Bebas Neue
                fontSize: "1.5rem", // Set font size to 1.5rem (same as "Daily Event - 3/27/2025")
                color: "black", // Match the color of other headings
              }}
            >
              Inventory Levels (Optional)
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Note: If you enter any field, all fields become required. Leave
              all blank if no incident occurred.
            </Typography>

            {inventoryRows.map((row, index) => {
              const requireAllFields = shouldRequireInventoryFields(row);

              return (
                <Paper key={row.id} elevation={1} sx={{ mb: 4, p: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Inventory Entry #{index + 1}
                  </Typography>

                  <Grid container spacing={2} justifyContent="space-between">
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Date"
                        name="date"
                        type="date"
                        required
                        value={row.date}
                        onChange={(e) => handleInventoryInputChange(row.id, e)}
                        disabled
                        InputLabelProps={{
                          shrink: true,
                        }}
                        size="small"
                        error={requireAllFields && !row.date}
                        helperText={
                          requireAllFields && !row.date ? "Required" : ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <FormControl
                        fullWidth
                        size="small"
                        required={requireAllFields}
                        error={requireAllFields && !row.itemDescription}
                      >
                        <InputLabel>Product Name</InputLabel>
                        <Select
                          name="itemDescription"
                          value={row.itemDescription}
                          label="Item Description"
                          onChange={(e) =>
                            handleInventorySelectChange(row.id, e)
                          }
                        >
                          <MenuItem value="">Select Product</MenuItem>
                          <MenuItem value="Angus Beef Burger Patty 6pcs">
                            Angus Beef Burger Patty 6pcs
                          </MenuItem>
                          <MenuItem value="All Beef Franks ">
                            All Beef Franks{" "}
                          </MenuItem>
                          <MenuItem value="Bonanza Nuggets Bites">
                            Bonanza Nuggets Bites
                          </MenuItem>
                          <MenuItem value="Bonanza Chicken Patty">
                            Bonanza Chicken Patty
                          </MenuItem>
                          <MenuItem value="Chicken Fingers">
                            Chicken Fingers
                          </MenuItem>
                          <MenuItem value="Chicken Turkey Hotdog">
                            Chicken Turkey Hotdog
                          </MenuItem>
                          <MenuItem value="Fish Fillet Sticks - breaded">
                            Fish Fillet Sticks - breaded
                          </MenuItem>
                          <MenuItem value="Margarine - Bakers Best ">
                            Margarine - Bakers Best{" "}
                          </MenuItem>
                          <MenuItem value="Barbecue Sauce">
                            Barbecue Sauce
                          </MenuItem>
                          <MenuItem value="Mccain Original Flat Chips skin-On">
                            Mccain Original Flat Chips skin-On
                          </MenuItem>
                          <MenuItem value="Swift Ham">Swift Ham</MenuItem>
                          <MenuItem value="Yowell Unsweetened Yoghurt">
                            Yowell Unsweetened Yoghurt
                          </MenuItem>
                          <MenuItem value="Potato Kingdom Fries">
                            Potato Kingdom Fries
                          </MenuItem>
                          <MenuItem value="Virginia Krunchy Chicken Poppers">
                            Virginia Krunchy Chicken Poppers
                          </MenuItem>
                          <MenuItem value="Señora Ulam Siomai 60pcs/pack (1KG) Beef">
                            Señora Ulam Siomai 60pcs/pack (1KG) Beef
                          </MenuItem>
                          <MenuItem value="Coney Island Sauce Premix (291731)">
                            Coney Island Sauce Premix (291731)
                          </MenuItem>
                          <MenuItem value="Popcorn Kernels - Preferred Butterfly Large - Indofine">
                            Popcorn Kernels - Preferred Butterfly Large -
                            Indofine
                          </MenuItem>
                          <MenuItem value="Popcorn Kernels - Morrison Butterfly Large - LL&Lance ">
                            Popcorn Kernels - Morrison Butterfly Large -
                            LL&Lance{" "}
                          </MenuItem>
                          <MenuItem value="Lightly Salted 500g">
                            Lightly Salted 500g
                          </MenuItem>
                          <MenuItem value="Tofu Chips Natural">
                            Tofu Chips Natural
                          </MenuItem>
                          <MenuItem value="Tofu Chips NC">
                            Tofu Chips NC
                          </MenuItem>
                          <MenuItem value="Tofu Chips SC">
                            Tofu Chips SC
                          </MenuItem>
                          <MenuItem value="Tofu Chips TB">
                            Tofu Chips TB
                          </MenuItem>
                          <MenuItem value="Tofu Chips WC">
                            Tofu Chips WC
                          </MenuItem>
                          <MenuItem value="Premium SuperPop-Buttered Caramel-Oh">
                            Premium SuperPop-Buttered Caramel-Oh
                          </MenuItem>
                          <MenuItem value="Premium SuperPop-Straw-very Cheesecake">
                            Premium SuperPop-Straw-very Cheesecake
                          </MenuItem>
                          <MenuItem value="Premium Superpop-OVAL-T KRUNCHHH!">
                            Premium Superpop-OVAL-T KRUNCHHH!
                          </MenuItem>
                          <MenuItem value="Powder - Cheese Sauce (Taters) (390134)">
                            Powder - Cheese Sauce (Taters) (390134)
                          </MenuItem>
                          <MenuItem value="Flavoring - Nacho Cheese (3kg) (390141)">
                            Flavoring - Nacho Cheese (3kg) (390141)
                          </MenuItem>
                          <MenuItem value="Flavoring - White Cheddar (3kg) (390143)">
                            Flavoring - White Cheddar (3kg) (390143)
                          </MenuItem>
                          <MenuItem value="Flavoring - Sour Cream & Chives (3kg) (390241)">
                            Flavoring - Sour Cream & Chives (3kg) (390241)
                          </MenuItem>
                          <MenuItem value="Flavoring - Texan BBQ (3kg) (390341)">
                            Flavoring - Texan BBQ (3kg) (390341)
                          </MenuItem>
                          <MenuItem value="Tomato Ketchup - Del Monte Original Blend (392001)">
                            Tomato Ketchup - Del Monte Original Blend (392001)
                          </MenuItem>
                          <MenuItem value="All-Purpose Cream - Nestle (361011)">
                            All-Purpose Cream - Nestle (361011)
                          </MenuItem>
                          <MenuItem value="Catsup - generic sachets (392061)">
                            Catsup - generic sachets (392061)
                          </MenuItem>
                          <MenuItem value="Mayonnaise - generic sachets 50s (392261)">
                            Mayonnaise - generic sachets 50s (392261)
                          </MenuItem>
                          <MenuItem value="Frito Plus Palm Cooking 17kgs">
                            Frito Plus Palm Cooking 17kgs
                          </MenuItem>
                          <MenuItem value="Iodized Salt (394531)">
                            Iodized Salt (394531)
                          </MenuItem>
                          <MenuItem value="Salt - generic sachets 100s (394561)">
                            Salt - generic sachets 100s (394561)
                          </MenuItem>
                          <MenuItem value="Vanilla Extract Flavor - McC (396141)">
                            Vanilla Extract Flavor - McC (396141)
                          </MenuItem>
                          <MenuItem value="Betacarotene Lucarotin 30 Sun 2kg/tin (396434)">
                            Betacarotene Lucarotin 30 Sun 2kg/tin (396434)
                          </MenuItem>
                          <MenuItem value="Jalapeno Peppers - La Costena sliced jalapeño peppers 2.8kg ">
                            Jalapeno Peppers - La Costena sliced jalapeño
                            peppers 2.8kg{" "}
                          </MenuItem>
                          <MenuItem value="Sugar Free Lemonade Powder Flavor">
                            Sugar Free Lemonade Powder Flavor
                          </MenuItem>
                          <MenuItem value="Plastic Sauce Lid - clear 44ml">
                            Plastic Sauce Lid - clear 44ml
                          </MenuItem>
                          <MenuItem value="Plastic Sauce Cup - white 44ml">
                            Plastic Sauce Cup - white 44ml
                          </MenuItem>
                          <MenuItem value="Plastic Sauce Cup - white 30ml">
                            Plastic Sauce Cup - white 30ml
                          </MenuItem>
                          <MenuItem value="Plastic Sauce Lid- white 30ml">
                            Plastic Sauce Lid- white 30ml
                          </MenuItem>
                          <MenuItem value="Plastic Lid - for 16/22oz cup (styrotech)">
                            Plastic Lid - for 16/22oz cup (styrotech)
                          </MenuItem>
                          <MenuItem value="Plastic Lid - for 12oz cup">
                            Plastic Lid - for 12oz cup
                          </MenuItem>
                          <MenuItem value="Paper Cup - Taters 12oz; 50pcs/sleeve ">
                            Paper Cup - Taters 12oz; 50pcs/sleeve{" "}
                          </MenuItem>
                          <MenuItem value="Paper Cup-Taters 22oz with print - 2017 Design (525172)">
                            Paper Cup-Taters 22oz with print - 2017 Design
                            (525172)
                          </MenuItem>
                          <MenuItem value="Paper Cup 16Oz 2017 Design (525165)">
                            Paper Cup 16Oz 2017 Design (525165)
                          </MenuItem>
                          <MenuItem value="Paper Cup 32oz 2017 Design (525153)">
                            Paper Cup 32oz 2017 Design (525153)
                          </MenuItem>
                          <MenuItem value="Plastic Lid - for 32oz cup">
                            Plastic Lid - for 32oz cup
                          </MenuItem>
                          <MenuItem value="Paper Wrap - Greaseproof plain lrg (15X15)">
                            Paper Wrap - Greaseproof plain lrg (15X15)
                          </MenuItem>
                          <MenuItem value="Paper Pouch - Hotdog Sandwich (plain)">
                            Paper Pouch - Hotdog Sandwich (plain)
                          </MenuItem>
                          <MenuItem value="Paper Wrap - Taters Large Fry Bag (136x109x35mm)">
                            Paper Wrap - Taters Large Fry Bag (136x109x35mm)
                          </MenuItem>
                          <MenuItem value="Paper Bag - Taters SOS Jr">
                            Paper Bag - Taters SOS Jr
                          </MenuItem>
                          <MenuItem value="PaperBag-TatersSOS#4 (2017)">
                            PaperBag-TatersSOS#4 (2017)
                          </MenuItem>
                          <MenuItem value="Paper Bag - Taters SOS#8">
                            Paper Bag - Taters SOS#8
                          </MenuItem>
                          <MenuItem value="Burger Box Printed 2017 (534312)">
                            Burger Box Printed 2017 (534312)
                          </MenuItem>
                          <MenuItem value="Chimara-Stand Pouch with Ziplock; 100pcs/pack (511200)">
                            Chimara-Stand Pouch with Ziplock; 100pcs/pack
                            (511200)
                          </MenuItem>
                          <MenuItem value="Paper Bag - Chimara SOS#4; 100pcs/bdl (whs to count 100pcs.b">
                            Paper Bag - Chimara SOS#4; 100pcs/bdl (whs to count
                            100pcs.b
                          </MenuItem>
                          <MenuItem value="Paper Bag - Chimara SOS#8 GR; 50pcs/bdl (512471)">
                            Paper Bag - Chimara SOS#8 GR; 50pcs/bdl (512471)
                          </MenuItem>
                          <MenuItem value="Chimara Take Out Box - Small; 100pcs/pack (534300)">
                            Chimara Take Out Box - Small; 100pcs/pack (534300)
                          </MenuItem>
                          <MenuItem value="Chimara Take Out Box - Medium; 100pcs/pack (534310)">
                            Chimara Take Out Box - Medium; 100pcs/pack (534310)
                          </MenuItem>
                          <MenuItem value="Large Tray (Box) - Carrier Board #15 w/print (size 15 3/8 x ">
                            Large Tray (Box) - Carrier Board #15 w/print (size
                            15 3/8 x{" "}
                          </MenuItem>
                          <MenuItem value="Hand Towel - Kau Thong (12s) (922021)">
                            Hand Towel - Kau Thong (12s) (922021)
                          </MenuItem>
                          <MenuItem value="Deogen - Suma Total 5L (923351)">
                            Deogen - Suma Total 5L (923351)
                          </MenuItem>
                          <MenuItem value="Divoplaq - Suma Total N3 5L (923451)">
                            Divoplaq - Suma Total N3 5L (923451)
                          </MenuItem>
                          <MenuItem value="Antibac - Diverseylever [by 100] (924011)">
                            Antibac - Diverseylever [by 100] (924011)
                          </MenuItem>
                          <MenuItem value="Detergent Powder - All-purpose 1kg (923240)">
                            Detergent Powder - All-purpose 1kg (923240)
                          </MenuItem>
                          <MenuItem value="Journal Tape (POS) 1ply 76mm x70mm (961211)">
                            Journal Tape (POS) 1ply 76mm x70mm (961211)
                          </MenuItem>
                          <MenuItem value="POS Journal Roll - ordinary 2ply 76mmx70mm (961230)">
                            POS Journal Roll - ordinary 2ply 76mmx70mm (961230)
                          </MenuItem>
                          <MenuItem value="POS Ribbon - ERC 38 black (generic) (962110)">
                            POS Ribbon - ERC 38 black (generic) (962110)
                          </MenuItem>
                          <MenuItem value="Paper Cup - 6.5oz plain(courtesy cup); 50pcs/sleeve (515130)">
                            Paper Cup - 6.5oz plain(courtesy cup); 50pcs/sleeve
                            (515130)
                          </MenuItem>
                          <MenuItem value="Plastic bag 20 x 30 Thicker (for popc); 100pcs/pack (542074)">
                            Plastic bag 20 x 30 Thicker (for popc); 100pcs/pack
                            (542074)
                          </MenuItem>
                          <MenuItem value="Biodegradable Sando Bags - Small; 100pcs/pack (542100)">
                            Biodegradable Sando Bags - Small; 100pcs/pack
                            (542100)
                          </MenuItem>
                          <MenuItem value="Biodegradable Sando Bags - Medium; 100pcs/pack (542110)">
                            Biodegradable Sando Bags - Medium; 100pcs/pack
                            (542110)
                          </MenuItem>
                          <MenuItem value="Biodegradable Sando Bags - Large;100pcs/pack (542120)">
                            Biodegradable Sando Bags - Large;100pcs/pack
                            (542120)
                          </MenuItem>
                          <MenuItem value="Plastic Resealable Bag Ziplock#7; 100pcs/pack (562050)">
                            Plastic Resealable Bag Ziplock#7; 100pcs/pack
                            (562050)
                          </MenuItem>
                          <MenuItem value="Plastic Resealable Bag Ziplock#8; 100pcs/pack (562060)">
                            Plastic Resealable Bag Ziplock#8; 100pcs/pack
                            (562060)
                          </MenuItem>
                          <MenuItem value="Plastic Resealable Bag Ziplock#11; 100pcs/pack (562080)">
                            Plastic Resealable Bag Ziplock#11; 100pcs/pack
                            (562080)
                          </MenuItem>
                          <MenuItem value="Plastic Resealable Bag Ziplock#9; 100pcs/pack (562070)">
                            Plastic Resealable Bag Ziplock#9; 100pcs/pack
                            (562070)
                          </MenuItem>
                          <MenuItem value="Plastic Straw - Transparent 1kg (Styrotech) (548022)">
                            Plastic Straw - Transparent 1kg (Styrotech) (548022)
                          </MenuItem>
                          <MenuItem value="Paper Bag- Brown Take Out#3 Plain (Kraft 80gsm) 100pcs/pack ">
                            Paper Bag- Brown Take Out#3 Plain (Kraft 80gsm)
                            100pcs/pack{" "}
                          </MenuItem>
                          <MenuItem value="Paper Bag Brown #8 (582861)">
                            Paper Bag Brown #8 (582861)
                          </MenuItem>
                          <MenuItem value="Paper Table Napkins - Taters Printed NEW">
                            Paper Table Napkins - Taters Printed NEW
                          </MenuItem>
                          <MenuItem value="Paper Towel - Interfolded Regular (Livi) (912211)">
                            Paper Towel - Interfolded Regular (Livi) (912211)
                          </MenuItem>
                          <MenuItem value="Plastic Gloves - Disposable transp. Sinmag (100s) (914011)">
                            Plastic Gloves - Disposable transp. Sinmag (100s)
                            (914011)
                          </MenuItem>
                          <MenuItem value="Trash Bag - Transparent XL (10pcs/roll) (921041)">
                            Trash Bag - Transparent XL (10pcs/roll) (921041)
                          </MenuItem>
                          <MenuItem value="Trash Bag - Transparent XXL (10pcs/roll) (921051)">
                            Trash Bag - Transparent XXL (10pcs/roll) (921051)
                          </MenuItem>
                          <MenuItem value="Trash Bag - Black XL (10pcs/roll) (921141)">
                            Trash Bag - Black XL (10pcs/roll) (921141)
                          </MenuItem>
                          <MenuItem value="   Paper Cup Genetic 16 oz (525160)">
                            {" "}
                            Paper Cup Genetic 16 oz (525160)
                          </MenuItem>
                          <MenuItem value="Taters Family  Ziplock (matte and glossy)">
                            Taters Family Ziplock (matte and glossy)
                          </MenuItem>
                          <MenuItem value="Taters Party Ziplock (matte and glossy)">
                            Taters Party Ziplock (matte and glossy)
                          </MenuItem>
                          <MenuItem value="Taters Mini Ziplock (Matte and Glossy)">
                            Taters Mini Ziplock (Matte and Glossy)
                          </MenuItem>
                          <MenuItem value="Plastic Lid Slim (90cm) 16/22oz Paper Cup (546060)">
                            Plastic Lid Slim (90cm) 16/22oz Paper Cup (546060)
                          </MenuItem>
                          <MenuItem value="Plain 16oz Paper Cup Slim (90cm)">
                            Plain 16oz Paper Cup Slim (90cm)
                          </MenuItem>
                          <MenuItem value="Plain 220z Paper Cup Slim (90cm)">
                            Plain 220z Paper Cup Slim (90cm)
                          </MenuItem>
                          <MenuItem value="Paper Wrap - Generic Large Fry Bag (136x109x35mm) (511370)">
                            Paper Wrap - Generic Large Fry Bag (136x109x35mm)
                            (511370)
                          </MenuItem>
                          <MenuItem value="3oz Paper Sauce Cup">
                            3oz Paper Sauce Cup
                          </MenuItem>
                          <MenuItem value="Small Hole Paper Straw Plain">
                            Small Hole Paper Straw Plain
                          </MenuItem>
                          <MenuItem value="Solupak Silica in OPP-Opaque 5g">
                            Solupak Silica in OPP-Opaque 5g
                          </MenuItem>
                          <MenuItem value="Solupak Silica in OPP-Opaque 2g">
                            Solupak Silica in OPP-Opaque 2g
                          </MenuItem>
                          <MenuItem value="Ziplock Standing Plain 12cm x 20cm">
                            Ziplock Standing Plain 12cm x 20cm
                          </MenuItem>
                          <MenuItem value="Ziplock Standing Plain 11cm x 18 cm">
                            Ziplock Standing Plain 11cm x 18 cm
                          </MenuItem>
                          <MenuItem value="Argentina Corned Beef 175gx48">
                            Argentina Corned Beef 175gx48
                          </MenuItem>
                          <MenuItem value="Yoghurt Syrup">
                            Yoghurt Syrup
                          </MenuItem>
                          <MenuItem value="Tofu Chips Ziplock Packaging">
                            Tofu Chips Ziplock Packaging
                          </MenuItem>
                          <MenuItem value="Tortilla Chips Ziplock Packaging">
                            Tortilla Chips Ziplock Packaging
                          </MenuItem>
                          <MenuItem value="Juice (Square 500ml) PET Bottle">
                            Juice (Square 500ml) PET Bottle
                          </MenuItem>
                          <MenuItem value="Trash Bag-Green XL (10pcs/roll)">
                            Trash Bag-Green XL (10pcs/roll)
                          </MenuItem>
                          <MenuItem value="Plastic bag 20 x 30 Thicker (for popc); 100pcs/pack (542074)">
                            Plastic bag 20 x 30 Thicker (for popc); 100pcs/pack
                            (542074)
                          </MenuItem>
                          <MenuItem value="Sweet Grapes Syrup 750ml">
                            Sweet Grapes Syrup 750ml
                          </MenuItem>
                          <MenuItem value="Potato Chips-McCain Original 1.8144kg">
                            Potato Chips-McCain Original 1.8144kg
                          </MenuItem>
                        </Select>
                        {requireAllFields && !row.itemDescription && (
                          <Typography color="error" variant="caption">
                            Required
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <FormControl
                        fullWidth
                        size="small"
                        required={requireAllFields}
                        error={requireAllFields && !row.criticalLevel}
                      >
                        <InputLabel>Critical Level</InputLabel>
                        <Select
                          name="criticalLevel"
                          value={row.criticalLevel}
                          label="Critical Level"
                          onChange={(e) =>
                            handleInventorySelectChange(row.id, e)
                          }
                        >
                          <MenuItem value="10">10</MenuItem>
                        </Select>
                        {requireAllFields && !row.criticalLevel && (
                          <Typography color="error" variant="caption">
                            Required
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <NumberInput
                        label="Qty"
                        value={row.quantity}
                        onChange={(value) =>
                          handleInventoryInputChange(row.id, {
                            target: { name: "quantity", value },
                          } as any)
                        }
                        required={requireAllFields}
                        error={requireAllFields && !row.quantity}
                        helperText={
                          requireAllFields && !row.quantity ? "Required" : ""
                        }
                        min={0} // Optional: Set minimum value
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        label="Expiry Date"
                        name="expiryDate"
                        type="date"
                        required={requireAllFields}
                        value={row.expiryDate}
                        onChange={(e) => handleInventoryInputChange(row.id, e)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        size="small"
                        error={requireAllFields && !row.expiryDate}
                        helperText={
                          requireAllFields && !row.expiryDate ? "Required" : ""
                        }
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={1}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: { xs: "flex-start", sm: "center" },
                      }}
                    >
                      <IconButton
                        color="error"
                        onClick={() => removeInventoryRow(row.id)}
                        disabled={inventoryRows.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}

            <Box sx={{ display: "flex", gap: 2, mt: 2, mb: 2 }}>
              <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={addInventoryRow}
                variant="outlined"
                sx={{ mt: 1 }}
              >
                Add Inventory Entry
              </Button>
              <Button
                startIcon={<RemoveCircleOutlineIcon />}
                variant="outlined"
                color="secondary"
                onClick={clearInventoryFields}
                sx={{ mt: 1 }}
              >
                CLEAR FIELDS
              </Button>
            </Box>
          </Box>
        );
      case 3:
        const clearEfficiencyFields = () => {
          setEfficiencyRows([
            {
              id: generateUniqueId(),
              store_code: "",
              date: "",
              shift: "",
              accountable: "",
              itemStockout: "",
              itemDescription: "",
              correctiveAction: "",
              preventiveAction: "",
            },
          ]);
        };

        return (
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: "'Bebas Neue', sans-serif", // Change font to Bebas Neue
                fontSize: "1.5rem", // Set font size to 1.5rem (same as "Daily Event - 3/27/2025")
                color: "black", // Match the color of other headings
              }}
            >
              Efficiency Metrics (Optional)
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Note: If you enter any field, all fields become required. Leave
              all blank if no incident occurred.
            </Typography>

            {efficiencyRows.map((row, index) => {
              const requireAllFields = shouldRequireEfficiencyFields(row);

              return (
                <Paper key={row.id} elevation={1} sx={{ mb: 4, p: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Efficiency Entry #{index + 1}
                  </Typography>

                  {/* Basic Information */}
                  <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Date"
                        name="date"
                        type="date"
                        required
                        value={row.date}
                        onChange={(e) => handleEfficiencyInputChange(row.id, e)}
                        disabled
                        InputLabelProps={{
                          shrink: true,
                        }}
                        size="small"
                        error={requireAllFields && !row.date}
                        helperText={
                          requireAllFields && !row.date ? "Required" : ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl
                        fullWidth
                        size="small"
                        required={requireAllFields}
                        error={requireAllFields && !row.shift}
                      >
                        <InputLabel>Shift</InputLabel>
                        <Select
                          name="shift"
                          value={row.shift}
                          label="Shift"
                          onChange={(e) =>
                            handleEfficiencySelectChange(row.id, e)
                          }
                        >
                          <MenuItem value="AM">AM</MenuItem>
                          <MenuItem value="PM">PM</MenuItem>
                        </Select>
                        {requireAllFields && !row.shift && (
                          <Typography color="error" variant="caption">
                            Required
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl
                        fullWidth
                        size="small"
                        required={requireAllFields}
                        error={requireAllFields && !row.accountable}
                      >
                        <InputLabel>Accountable</InputLabel>
                        <Select
                          name="accountable"
                          value={row.accountable}
                          label="Accountable"
                          onChange={(e) =>
                            handleEfficiencySelectChange(row.id, e)
                          }
                        >
                          {employee_data.map((emp) => (
                            <MenuItem key={emp.id} value={emp.id.toString()}>
                              {emp.name || emp.id.toString()}
                            </MenuItem>
                          ))}
                        </Select>
                        {requireAllFields && !row.accountable && (
                          <Typography color="error" variant="caption">
                            Required
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <StockoutInput
                        label="Item Stockout"
                        value={row.itemStockout}
                        onChange={(value) =>
                          handleEfficiencyInputChange(row.id, {
                            target: { name: "itemStockout", value },
                          } as any)
                        }
                        required={requireAllFields}
                        error={requireAllFields && !row.itemStockout}
                        helperText={
                          requireAllFields && !row.itemStockout
                            ? "Required"
                            : ""
                        }
                        min={0} // Optional: Set minimum value
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  {/* Stock Details */}
                  <Typography variant="subtitle1" gutterBottom>
                    Stock Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                      <FormControl
                        fullWidth
                        size="small"
                        required={requireAllFields}
                        error={requireAllFields && !row.itemDescription}
                      >
                        <InputLabel>Item Description</InputLabel>
                        <Select
                          name="itemDescription"
                          value={row.itemDescription}
                          label="Item Description"
                          onChange={(e) =>
                            handleEfficiencySelectChange(row.id, e)
                          }
                        >
                          <MenuItem value="">Select Product</MenuItem>
                          <MenuItem value="Angus Beef Burger Patty 6pcs">
                            Angus Beef Burger Patty 6pcs
                          </MenuItem>
                          <MenuItem value="All Beef Franks ">
                            All Beef Franks{" "}
                          </MenuItem>
                          <MenuItem value="Bonanza Nuggets Bites">
                            Bonanza Nuggets Bites
                          </MenuItem>
                          <MenuItem value="Bonanza Chicken Patty">
                            Bonanza Chicken Patty
                          </MenuItem>
                          <MenuItem value="Chicken Fingers">
                            Chicken Fingers
                          </MenuItem>
                          <MenuItem value="Chicken Turkey Hotdog">
                            Chicken Turkey Hotdog
                          </MenuItem>
                          <MenuItem value="Fish Fillet Sticks - breaded">
                            Fish Fillet Sticks - breaded
                          </MenuItem>
                          <MenuItem value="Margarine - Bakers Best ">
                            Margarine - Bakers Best{" "}
                          </MenuItem>
                          <MenuItem value="Barbecue Sauce">
                            Barbecue Sauce
                          </MenuItem>
                          <MenuItem value="Mccain Original Flat Chips skin-On">
                            Mccain Original Flat Chips skin-On
                          </MenuItem>
                          <MenuItem value="Swift Ham">Swift Ham</MenuItem>
                          <MenuItem value="Yowell Unsweetened Yoghurt">
                            Yowell Unsweetened Yoghurt
                          </MenuItem>
                          <MenuItem value="Potato Kingdom Fries">
                            Potato Kingdom Fries
                          </MenuItem>
                          <MenuItem value="Virginia Krunchy Chicken Poppers">
                            Virginia Krunchy Chicken Poppers
                          </MenuItem>
                          <MenuItem value="Señora Ulam Siomai 60pcs/pack (1KG) Beef">
                            Señora Ulam Siomai 60pcs/pack (1KG) Beef
                          </MenuItem>
                          <MenuItem value="Coney Island Sauce Premix (291731)">
                            Coney Island Sauce Premix (291731)
                          </MenuItem>
                          <MenuItem value="Popcorn Kernels - Preferred Butterfly Large - Indofine">
                            Popcorn Kernels - Preferred Butterfly Large -
                            Indofine
                          </MenuItem>
                          <MenuItem value="Popcorn Kernels - Morrison Butterfly Large - LL&Lance ">
                            Popcorn Kernels - Morrison Butterfly Large -
                            LL&Lance{" "}
                          </MenuItem>
                          <MenuItem value="Lightly Salted 500g">
                            Lightly Salted 500g
                          </MenuItem>
                          <MenuItem value="Tofu Chips Natural">
                            Tofu Chips Natural
                          </MenuItem>
                          <MenuItem value="Tofu Chips NC">
                            Tofu Chips NC
                          </MenuItem>
                          <MenuItem value="Tofu Chips SC">
                            Tofu Chips SC
                          </MenuItem>
                          <MenuItem value="Tofu Chips TB">
                            Tofu Chips TB
                          </MenuItem>
                          <MenuItem value="Tofu Chips WC">
                            Tofu Chips WC
                          </MenuItem>
                          <MenuItem value="Premium SuperPop-Buttered Caramel-Oh">
                            Premium SuperPop-Buttered Caramel-Oh
                          </MenuItem>
                          <MenuItem value="Premium SuperPop-Straw-very Cheesecake">
                            Premium SuperPop-Straw-very Cheesecake
                          </MenuItem>
                          <MenuItem value="Premium Superpop-OVAL-T KRUNCHHH!">
                            Premium Superpop-OVAL-T KRUNCHHH!
                          </MenuItem>
                          <MenuItem value="Powder - Cheese Sauce (Taters) (390134)">
                            Powder - Cheese Sauce (Taters) (390134)
                          </MenuItem>
                          <MenuItem value="Flavoring - Nacho Cheese (3kg) (390141)">
                            Flavoring - Nacho Cheese (3kg) (390141)
                          </MenuItem>
                          <MenuItem value="Flavoring - White Cheddar (3kg) (390143)">
                            Flavoring - White Cheddar (3kg) (390143)
                          </MenuItem>
                          <MenuItem value="Flavoring - Sour Cream & Chives (3kg) (390241)">
                            Flavoring - Sour Cream & Chives (3kg) (390241)
                          </MenuItem>
                          <MenuItem value="Flavoring - Texan BBQ (3kg) (390341)">
                            Flavoring - Texan BBQ (3kg) (390341)
                          </MenuItem>
                          <MenuItem value="Tomato Ketchup - Del Monte Original Blend (392001)">
                            Tomato Ketchup - Del Monte Original Blend (392001)
                          </MenuItem>
                          <MenuItem value="All-Purpose Cream - Nestle (361011)">
                            All-Purpose Cream - Nestle (361011)
                          </MenuItem>
                          <MenuItem value="Catsup - generic sachets (392061)">
                            Catsup - generic sachets (392061)
                          </MenuItem>
                          <MenuItem value="Mayonnaise - generic sachets 50s (392261)">
                            Mayonnaise - generic sachets 50s (392261)
                          </MenuItem>
                          <MenuItem value="Frito Plus Palm Cooking 17kgs">
                            Frito Plus Palm Cooking 17kgs
                          </MenuItem>
                          <MenuItem value="Iodized Salt (394531)">
                            Iodized Salt (394531)
                          </MenuItem>
                          <MenuItem value="Salt - generic sachets 100s (394561)">
                            Salt - generic sachets 100s (394561)
                          </MenuItem>
                          <MenuItem value="Vanilla Extract Flavor - McC (396141)">
                            Vanilla Extract Flavor - McC (396141)
                          </MenuItem>
                          <MenuItem value="Betacarotene Lucarotin 30 Sun 2kg/tin (396434)">
                            Betacarotene Lucarotin 30 Sun 2kg/tin (396434)
                          </MenuItem>
                          <MenuItem value="Jalapeno Peppers - La Costena sliced jalapeño peppers 2.8kg ">
                            Jalapeno Peppers - La Costena sliced jalapeño
                            peppers 2.8kg{" "}
                          </MenuItem>
                          <MenuItem value="Sugar Free Lemonade Powder Flavor">
                            Sugar Free Lemonade Powder Flavor
                          </MenuItem>
                          <MenuItem value="Plastic Sauce Lid - clear 44ml">
                            Plastic Sauce Lid - clear 44ml
                          </MenuItem>
                          <MenuItem value="Plastic Sauce Cup - white 44ml">
                            Plastic Sauce Cup - white 44ml
                          </MenuItem>
                          <MenuItem value="Plastic Sauce Cup - white 30ml">
                            Plastic Sauce Cup - white 30ml
                          </MenuItem>
                          <MenuItem value="Plastic Sauce Lid- white 30ml">
                            Plastic Sauce Lid- white 30ml
                          </MenuItem>
                          <MenuItem value="Plastic Lid - for 16/22oz cup (styrotech)">
                            Plastic Lid - for 16/22oz cup (styrotech)
                          </MenuItem>
                          <MenuItem value="Plastic Lid - for 12oz cup">
                            Plastic Lid - for 12oz cup
                          </MenuItem>
                          <MenuItem value="Paper Cup - Taters 12oz; 50pcs/sleeve ">
                            Paper Cup - Taters 12oz; 50pcs/sleeve{" "}
                          </MenuItem>
                          <MenuItem value="Paper Cup-Taters 22oz with print - 2017 Design (525172)">
                            Paper Cup-Taters 22oz with print - 2017 Design
                            (525172)
                          </MenuItem>
                          <MenuItem value="Paper Cup 16Oz 2017 Design (525165)">
                            Paper Cup 16Oz 2017 Design (525165)
                          </MenuItem>
                          <MenuItem value="Paper Cup 32oz 2017 Design (525153)">
                            Paper Cup 32oz 2017 Design (525153)
                          </MenuItem>
                          <MenuItem value="Plastic Lid - for 32oz cup">
                            Plastic Lid - for 32oz cup
                          </MenuItem>
                          <MenuItem value="Paper Wrap - Greaseproof plain lrg (15X15)">
                            Paper Wrap - Greaseproof plain lrg (15X15)
                          </MenuItem>
                          <MenuItem value="Paper Pouch - Hotdog Sandwich (plain)">
                            Paper Pouch - Hotdog Sandwich (plain)
                          </MenuItem>
                          <MenuItem value="Paper Wrap - Taters Large Fry Bag (136x109x35mm)">
                            Paper Wrap - Taters Large Fry Bag (136x109x35mm)
                          </MenuItem>
                          <MenuItem value="Paper Bag - Taters SOS Jr">
                            Paper Bag - Taters SOS Jr
                          </MenuItem>
                          <MenuItem value="PaperBag-TatersSOS#4 (2017)">
                            PaperBag-TatersSOS#4 (2017)
                          </MenuItem>
                          <MenuItem value="Paper Bag - Taters SOS#8">
                            Paper Bag - Taters SOS#8
                          </MenuItem>
                          <MenuItem value="Burger Box Printed 2017 (534312)">
                            Burger Box Printed 2017 (534312)
                          </MenuItem>
                          <MenuItem value="Chimara-Stand Pouch with Ziplock; 100pcs/pack (511200)">
                            Chimara-Stand Pouch with Ziplock; 100pcs/pack
                            (511200)
                          </MenuItem>
                          <MenuItem value="Paper Bag - Chimara SOS#4; 100pcs/bdl (whs to count 100pcs.b">
                            Paper Bag - Chimara SOS#4; 100pcs/bdl (whs to count
                            100pcs.b
                          </MenuItem>
                          <MenuItem value="Paper Bag - Chimara SOS#8 GR; 50pcs/bdl (512471)">
                            Paper Bag - Chimara SOS#8 GR; 50pcs/bdl (512471)
                          </MenuItem>
                          <MenuItem value="Chimara Take Out Box - Small; 100pcs/pack (534300)">
                            Chimara Take Out Box - Small; 100pcs/pack (534300)
                          </MenuItem>
                          <MenuItem value="Chimara Take Out Box - Medium; 100pcs/pack (534310)">
                            Chimara Take Out Box - Medium; 100pcs/pack (534310)
                          </MenuItem>
                          <MenuItem value="Large Tray (Box) - Carrier Board #15 w/print (size 15 3/8 x ">
                            Large Tray (Box) - Carrier Board #15 w/print (size
                            15 3/8 x{" "}
                          </MenuItem>
                          <MenuItem value="Hand Towel - Kau Thong (12s) (922021)">
                            Hand Towel - Kau Thong (12s) (922021)
                          </MenuItem>
                          <MenuItem value="Deogen - Suma Total 5L (923351)">
                            Deogen - Suma Total 5L (923351)
                          </MenuItem>
                          <MenuItem value="Divoplaq - Suma Total N3 5L (923451)">
                            Divoplaq - Suma Total N3 5L (923451)
                          </MenuItem>
                          <MenuItem value="Antibac - Diverseylever [by 100] (924011)">
                            Antibac - Diverseylever [by 100] (924011)
                          </MenuItem>
                          <MenuItem value="Detergent Powder - All-purpose 1kg (923240)">
                            Detergent Powder - All-purpose 1kg (923240)
                          </MenuItem>
                          <MenuItem value="Journal Tape (POS) 1ply 76mm x70mm (961211)">
                            Journal Tape (POS) 1ply 76mm x70mm (961211)
                          </MenuItem>
                          <MenuItem value="POS Journal Roll - ordinary 2ply 76mmx70mm (961230)">
                            POS Journal Roll - ordinary 2ply 76mmx70mm (961230)
                          </MenuItem>
                          <MenuItem value="POS Ribbon - ERC 38 black (generic) (962110)">
                            POS Ribbon - ERC 38 black (generic) (962110)
                          </MenuItem>
                          <MenuItem value="Paper Cup - 6.5oz plain(courtesy cup); 50pcs/sleeve (515130)">
                            Paper Cup - 6.5oz plain(courtesy cup); 50pcs/sleeve
                            (515130)
                          </MenuItem>
                          <MenuItem value="Plastic bag 20 x 30 Thicker (for popc); 100pcs/pack (542074)">
                            Plastic bag 20 x 30 Thicker (for popc); 100pcs/pack
                            (542074)
                          </MenuItem>
                          <MenuItem value="Biodegradable Sando Bags - Small; 100pcs/pack (542100)">
                            Biodegradable Sando Bags - Small; 100pcs/pack
                            (542100)
                          </MenuItem>
                          <MenuItem value="Biodegradable Sando Bags - Medium; 100pcs/pack (542110)">
                            Biodegradable Sando Bags - Medium; 100pcs/pack
                            (542110)
                          </MenuItem>
                          <MenuItem value="Biodegradable Sando Bags - Large;100pcs/pack (542120)">
                            Biodegradable Sando Bags - Large;100pcs/pack
                            (542120)
                          </MenuItem>
                          <MenuItem value="Plastic Resealable Bag Ziplock#7; 100pcs/pack (562050)">
                            Plastic Resealable Bag Ziplock#7; 100pcs/pack
                            (562050)
                          </MenuItem>
                          <MenuItem value="Plastic Resealable Bag Ziplock#8; 100pcs/pack (562060)">
                            Plastic Resealable Bag Ziplock#8; 100pcs/pack
                            (562060)
                          </MenuItem>
                          <MenuItem value="Plastic Resealable Bag Ziplock#11; 100pcs/pack (562080)">
                            Plastic Resealable Bag Ziplock#11; 100pcs/pack
                            (562080)
                          </MenuItem>
                          <MenuItem value="Plastic Resealable Bag Ziplock#9; 100pcs/pack (562070)">
                            Plastic Resealable Bag Ziplock#9; 100pcs/pack
                            (562070)
                          </MenuItem>
                          <MenuItem value="Plastic Straw - Transparent 1kg (Styrotech) (548022)">
                            Plastic Straw - Transparent 1kg (Styrotech) (548022)
                          </MenuItem>
                          <MenuItem value="Paper Bag- Brown Take Out#3 Plain (Kraft 80gsm) 100pcs/pack ">
                            Paper Bag- Brown Take Out#3 Plain (Kraft 80gsm)
                            100pcs/pack{" "}
                          </MenuItem>
                          <MenuItem value="Paper Bag Brown #8 (582861)">
                            Paper Bag Brown #8 (582861)
                          </MenuItem>
                          <MenuItem value="Paper Table Napkins - Taters Printed NEW">
                            Paper Table Napkins - Taters Printed NEW
                          </MenuItem>
                          <MenuItem value="Paper Towel - Interfolded Regular (Livi) (912211)">
                            Paper Towel - Interfolded Regular (Livi) (912211)
                          </MenuItem>
                          <MenuItem value="Plastic Gloves - Disposable transp. Sinmag (100s) (914011)">
                            Plastic Gloves - Disposable transp. Sinmag (100s)
                            (914011)
                          </MenuItem>
                          <MenuItem value="Trash Bag - Transparent XL (10pcs/roll) (921041)">
                            Trash Bag - Transparent XL (10pcs/roll) (921041)
                          </MenuItem>
                          <MenuItem value="Trash Bag - Transparent XXL (10pcs/roll) (921051)">
                            Trash Bag - Transparent XXL (10pcs/roll) (921051)
                          </MenuItem>
                          <MenuItem value="Trash Bag - Black XL (10pcs/roll) (921141)">
                            Trash Bag - Black XL (10pcs/roll) (921141)
                          </MenuItem>
                          <MenuItem value="   Paper Cup Genetic 16 oz (525160)">
                            {" "}
                            Paper Cup Genetic 16 oz (525160)
                          </MenuItem>
                          <MenuItem value="Taters Family  Ziplock (matte and glossy)">
                            Taters Family Ziplock (matte and glossy)
                          </MenuItem>
                          <MenuItem value="Taters Party Ziplock (matte and glossy)">
                            Taters Party Ziplock (matte and glossy)
                          </MenuItem>
                          <MenuItem value="Taters Mini Ziplock (Matte and Glossy)">
                            Taters Mini Ziplock (Matte and Glossy)
                          </MenuItem>
                          <MenuItem value="Plastic Lid Slim (90cm) 16/22oz Paper Cup (546060)">
                            Plastic Lid Slim (90cm) 16/22oz Paper Cup (546060)
                          </MenuItem>
                          <MenuItem value="Plain 16oz Paper Cup Slim (90cm)">
                            Plain 16oz Paper Cup Slim (90cm)
                          </MenuItem>
                          <MenuItem value="Plain 220z Paper Cup Slim (90cm)">
                            Plain 220z Paper Cup Slim (90cm)
                          </MenuItem>
                          <MenuItem value="Paper Wrap - Generic Large Fry Bag (136x109x35mm) (511370)">
                            Paper Wrap - Generic Large Fry Bag (136x109x35mm)
                            (511370)
                          </MenuItem>
                          <MenuItem value="3oz Paper Sauce Cup">
                            3oz Paper Sauce Cup
                          </MenuItem>
                          <MenuItem value="Small Hole Paper Straw Plain">
                            Small Hole Paper Straw Plain
                          </MenuItem>
                          <MenuItem value="Solupak Silica in OPP-Opaque 5g">
                            Solupak Silica in OPP-Opaque 5g
                          </MenuItem>
                          <MenuItem value="Solupak Silica in OPP-Opaque 2g">
                            Solupak Silica in OPP-Opaque 2g
                          </MenuItem>
                          <MenuItem value="Ziplock Standing Plain 12cm x 20cm">
                            Ziplock Standing Plain 12cm x 20cm
                          </MenuItem>
                          <MenuItem value="Ziplock Standing Plain 11cm x 18 cm">
                            Ziplock Standing Plain 11cm x 18 cm
                          </MenuItem>
                          <MenuItem value="Argentina Corned Beef 175gx48">
                            Argentina Corned Beef 175gx48
                          </MenuItem>
                          <MenuItem value="Yoghurt Syrup">
                            Yoghurt Syrup
                          </MenuItem>
                          <MenuItem value="Tofu Chips Ziplock Packaging">
                            Tofu Chips Ziplock Packaging
                          </MenuItem>
                          <MenuItem value="Tortilla Chips Ziplock Packaging">
                            Tortilla Chips Ziplock Packaging
                          </MenuItem>
                          <MenuItem value="Juice (Square 500ml) PET Bottle">
                            Juice (Square 500ml) PET Bottle
                          </MenuItem>
                          <MenuItem value="Trash Bag-Green XL (10pcs/roll)">
                            Trash Bag-Green XL (10pcs/roll)
                          </MenuItem>
                          <MenuItem value="Plastic bag 20 x 30 Thicker (for popc); 100pcs/pack (542074)">
                            Plastic bag 20 x 30 Thicker (for popc); 100pcs/pack
                            (542074)
                          </MenuItem>
                          <MenuItem value="Sweet Grapes Syrup 750ml">
                            Sweet Grapes Syrup 750ml
                          </MenuItem>
                          <MenuItem value="Potato Chips-McCain Original 1.8144kg">
                            Potato Chips-McCain Original 1.8144kg
                          </MenuItem>
                        </Select>
                        {requireAllFields && !row.itemDescription && (
                          <Typography color="error" variant="caption">
                            Required
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  {/* Actions */}
                  <Typography variant="subtitle1" gutterBottom>
                    Actions
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Corrective Action
                      </Typography>
                      <TextareaAutosize
                        aria-label="correctiveAction"
                        name="correctiveAction"
                        minRows={3}
                        placeholder="Enter corrective action"
                        value={row.correctiveAction}
                        onChange={(e) => handleEfficiencyInputChange(row.id, e)}
                        style={{
                          width: "100%",
                          padding: "8px",
                          fontFamily: "inherit",
                          fontSize: "0.875rem",
                          borderRadius: "4px",
                          borderColor:
                            requireAllFields && !row.correctiveAction
                              ? "#f44336"
                              : "rgba(0, 0, 0, 0.23)",
                        }}
                        required={requireAllFields}
                      />
                      {requireAllFields && !row.correctiveAction && (
                        <Typography color="error" variant="caption">
                          Required
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Preventive Action
                      </Typography>
                      <TextareaAutosize
                        aria-label="preventiveAction"
                        name="preventiveAction"
                        minRows={3}
                        placeholder="Enter preventive action"
                        value={row.preventiveAction}
                        onChange={(e) => handleEfficiencyInputChange(row.id, e)}
                        style={{
                          width: "100%",
                          padding: "8px",
                          fontFamily: "inherit",
                          fontSize: "0.875rem",
                          borderRadius: "4px",
                          borderColor:
                            requireAllFields && !row.preventiveAction
                              ? "#f44336"
                              : "rgba(0, 0, 0, 0.23)",
                        }}
                        required={requireAllFields}
                      />
                      {requireAllFields && !row.preventiveAction && (
                        <Typography color="error" variant="caption">
                          Required
                        </Typography>
                      )}
                    </Grid>
                  </Grid>

                  <Box
                    sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}
                  >
                    <IconButton
                      color="error"
                      onClick={() => removeEfficiencyRow(row.id)}
                      disabled={efficiencyRows.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Paper>
              );
            })}

            <Box sx={{ display: "flex", gap: 2, mt: 2, mb: 2 }}>
              <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={addEfficiencyRow}
                variant="outlined"
                sx={{ mt: 1 }}
              >
                Add Efficiency Entry
              </Button>
              <Button
                startIcon={<RemoveCircleOutlineIcon />}
                variant="outlined"
                color="secondary"
                onClick={clearEfficiencyFields}
                sx={{ mt: 1 }}
              >
                CLEAR FIELDS
              </Button>
            </Box>
          </Box>
        );
      case 4:
        const clearSvrFields = () => {
          setSvrRows([
            {
              id: generateUniqueId(),
              store_code: "",
              date: "",
              shift: "",
              cashier: "",
              declaredCash: "",
              calculatedCash: "",
              netSpecialSales: "",
              cashOverShort: "",
              dateTransaction: "",
              cashDeposit: "",
              varianceDeclaredDeposited: "",
              otherDeposits: "",
              creditCardSales: "",
              creditCardCharge: "",
              crMemo: "",
              gc: "",
              paymaya: "",
              vipSold: "",
              vip: "",
              centuryShopaholicVouchers: "",
              gcash: "",
              foodPanda: "",
              honestBee: "",
              metrodealNet: "",
              marketing: "",
              other: "",
              lazada: "",
              shopee: "",
              grab: "",
              booky: "",
              poodtrip: "",
              pickaroo: "",
              parahero: "",
              rareFoodShop: "",
              shopeePay: "",
              metroMart: "",
              zalora: "",
              eatigo: "",
              madison: "",
              smOnline: "",
              arExe: "",
              robinsons: "",
              catering: "",
              snackshop: "",
              reseller: "",
              arOps: "",
              arsmLifestyle: "",
              arBellevuehotel: "",
              offsite: "",
              happyappWallet: "",
              hyperMart: "",
              leePlaza: "",
              uymatiaoShipping: "",
              allEasy: "",
              arAct: "",
              shareTreat: "",
              totalnonCash: "",
              totalPayments: "",
              bulkWholesale: "",
              othersSpecify: "",
              cateringManual: "",
              offsiteSelling: "",
              resellerManual: "",
              snackshopManual: "",
              deliveryfeeManual: "",
              cartSales: "",
              consignment: "",
              totalSpecialSales: "",
              overallSales: "",
              srCitizen: "",
              pwdDisc: "",
              otherDisc: "",
              transactionCountPos: "",
              transactionValue: "",
              gcOriginatingStore: "",
              gcSerialNumber: "",
              terminalId: "",
              voids: "",
            },
          ]);
        };

        // Helper function to determine input type based on field name
        const getInputType = (fieldName: string) => {
          if (fieldName === "dateTransaction") {
            return "date";
          }
          return "number"; // Most fields in SVR are monetary/numeric
        };

        // Helper function to format field name into a label
        const formatFieldLabel = (fieldName: string) => {
          return fieldName
            .replace(/([A-Z])/g, " $1") // Add space before capital letters
            .replace(/^./, (str) => str.toUpperCase()); // Capitalize first letter
        };

        return (
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: "'Bebas Neue', sans-serif", // Change font to Bebas Neue
                fontSize: "1.5rem", // Set font size to 1.5rem (same as "Daily Event - 3/27/2025")
                color: "black", // Match the color of other headings
              }}
            >
              SVR Form (Daily)
            </Typography>

            {svrRows.map((row, index) => (
              <Paper key={row.id} elevation={1} sx={{ mb: 4, p: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  SVR Entry #{index + 1}
                </Typography>

                {/* Basic Information */}
                <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Date"
                      name="date"
                      type="date"
                      required
                      value={row.date}
                      onChange={(e) => handleSvrInputChange(row.id, e)}
                      disabled
                      InputLabelProps={{
                        shrink: true,
                      }}
                      size="small"
                      error={!row.date}
                      helperText={!row.date ? "This field is mandatory" : ""}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Date of Transaction"
                      name="dateTransaction"
                      type="date"
                      required
                      value={row.dateTransaction}
                      onChange={(e) => handleSvrInputChange(row.id, e)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      error={!row.dateTransaction}
                      helperText={
                        !row.dateTransaction ? "This field is mandatory" : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small" required>
                      <InputLabel>Shift</InputLabel>
                      <Select
                        name="shift"
                        value={row.shift}
                        label="Shift"
                        onChange={(e) => handleSvrSelectChange(row.id, e)}
                        error={!row.shift}
                      >
                        <MenuItem value="AM">AM</MenuItem>
                        <MenuItem value="PM">PM</MenuItem>
                      </Select>
                      {!row.shift && (
                        <Typography color="error" variant="caption">
                          This field is mandatory
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small" required>
                      <InputLabel>Cashier</InputLabel>
                      <Select
                        name="cashier"
                        value={row.cashier}
                        label="Cashier"
                        onChange={(e) => handleSvrSelectChange(row.id, e)}
                        error={!row.cashier}
                      >
                        {employee_data.map((emp) => (
                          <MenuItem key={emp.id} value={emp.id.toString()}>
                            {emp.name || emp.id.toString()}
                          </MenuItem>
                        ))}
                      </Select>
                      {!row.cashier && (
                        <Typography color="error" variant="caption">
                          This field is mandatory
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Declared Cash"
                      name="declaredCash"
                      type="number"
                      required
                      value={row.declaredCash}
                      onChange={(e) => handleSvrInputChange(row.id, e)}
                      size="small"
                      error={!row.declaredCash}
                      helperText={
                        !row.declaredCash ? "This field is mandatory" : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Calculated Cash"
                      name="calculatedCash"
                      type="number"
                      required
                      value={row.calculatedCash}
                      onChange={(e) => handleSvrInputChange(row.id, e)}
                      size="small"
                      error={!row.calculatedCash}
                      helperText={
                        !row.calculatedCash ? "This field is mandatory" : ""
                      }
                    />
                  </Grid>
                </Grid>

                {/* Optional Fields (Visible based on Transfer List) */}
                {rightItems.length > 0 && (
                  <>
                    <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                      Additional Fields
                    </Typography>

                    {/* Render fields in rows of 4 */}
                    {Array(Math.ceil(rightItems.length / 4))
                      .fill(0)
                      .map((_, rowIndex) => (
                        <Grid
                          container
                          spacing={2}
                          key={rowIndex}
                          sx={{ mt: rowIndex > 0 ? 2 : 0 }}
                        >
                          {rightItems
                            .slice(rowIndex * 4, (rowIndex + 1) * 4)
                            .map((fieldName) => (
                              <Grid item xs={12} sm={3} key={fieldName}>
                                <TextField
                                  fullWidth
                                  label={formatFieldLabel(fieldName)}
                                  name={fieldName}
                                  type={getInputType(fieldName)}
                                  value={
                                    row[fieldName as keyof SvrFormRow] || ""
                                  }
                                  onChange={(e) =>
                                    handleSvrInputChange(row.id, e)
                                  }
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                  size="small"
                                />
                              </Grid>
                            ))}
                        </Grid>
                      ))}
                  </>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Transfer List */}
                <Typography variant="subtitle1" gutterBottom>
                  Select Visible Fields
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TransferList
                      leftItems={leftItems}
                      rightItems={rightItems}
                      onChange={handleTransferListChange}
                      itemLabel={(item) => {
                        // Convert camelCase to Title Case with spaces
                        const formatted = item
                          .replace(/([A-Z])/g, " $1")
                          .replace(/^./, (str) => str.toUpperCase());
                        return formatted;
                      }}
                    />
                  </Grid>
                </Grid>

                <Box
                  sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}
                >
                  <IconButton
                    color="error"
                    onClick={() => removeSvrRow(row.id)}
                    disabled={svrRows.length === 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Paper>
            ))}

            <Box sx={{ display: "flex", gap: 2, mt: 2, mb: 2 }}>
              <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={addSvrRow}
                variant="outlined"
                sx={{ mt: 1 }}
              >
                Add SVR Entry
              </Button>
              <Button
                startIcon={<RemoveCircleOutlineIcon />}
                variant="outlined"
                color="secondary"
                onClick={clearSvrFields}
                sx={{ mt: 1 }}
              >
                CLEAR FIELDS
              </Button>
            </Box>
          </Box>
        );

      default:
        return "Unknown step";
    }
  };

  // Render a day cell in the calendar
  // Replace the renderDayCell function with this enhanced version
  const renderDayCell = (day: CalendarDay, index: number) => {
    const isToday = day.date.toDateString() === new Date().toDateString();
    const isSelected =
      dateWasSelected &&
      day.date.toDateString() === selectedDate.toDateString();

    return (
      <Grid item xs={12 / 7} key={index}>
        <Box
          sx={{
            minHeight: "80px",
            p: 1,
            m: 0.5,
            border: isToday
              ? "2px solid #2196f3"
              : isSelected
              ? "2px solid #2196f3"
              : "1px solid #e0e0e0",
            backgroundColor:
              isToday && isSelected
                ? "#e3f2fd"
                : isToday
                ? "#f3e5f5"
                : isSelected
                ? "#e3f2fd"
                : day.isCurrentMonth
                ? "white"
                : "#f5f5f5",
            borderRadius: 1,
            cursor: "pointer",
            opacity: day.isCurrentMonth ? 1 : 0.6,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "#f0f0f0",
              transform: "translateY(-1px)",
            },
          }}
          onClick={() => handleDateSelect(day.date)}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: isToday ? "bold" : "normal",
              color: isToday ? "#2196f3" : "#333",
              mb: 0.5,
            }}
          >
            {day.date.getDate()}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
            {day.events.map((event, idx) => (
              <Button
                key={idx}
                variant="contained"
                size="small"
                fullWidth
                onClick={(e) => {
                  e.stopPropagation();
                  handleEventSelect(event);
                }}
                sx={{
                  py: 0.2,
                  minHeight: 0,
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  backgroundColor:
                    event === "Daily"
                      ? "#800000"
                      : event === "Weekly"
                      ? "#0F9D58"
                      : "#DC0000",
                  "&:hover": {
                    backgroundColor:
                      event === "Daily"
                        ? "#600000"
                        : event === "Weekly"
                        ? "#0A7A45"
                        : "#A50000",
                  },
                }}
              >
                {event}
              </Button>
            ))}
          </Box>
        </Box>
      </Grid>
    );
  };

  // Render the calendar view
  // Replace the entire renderCalendar function with this enhanced version
  // Replace the entire renderCalendar function with this much cleaner version
  // Replace the entire renderCalendar function with this beautifully redesigned version
  // Replace the entire renderCalendar function with this version that has a much better calendar grid
  const renderCalendar = () => {
    const days = getDaysForMonthView();

    return (
      <Box sx={{ width: "100%", mb: 4 }}>
        {/* Clean Header Section */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #2c3e50 0%, #3498db 100%)",
            borderRadius: 2,
            p: 4,
            mb: 3,
            color: "white",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: "2.5rem",
              mb: 1,
            }}
          >
            Select a Date to Manage Forms
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Choose any date from the calendar below to view or create daily
            reports
          </Typography>
        </Box>

        {/* Simple Statistics Row */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                textAlign: "center",
                borderLeft: "4px solid #800000",
                background: "#fff",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  color: "#800000",
                }}
              >
                {monthStats.dailyEvents}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Daily Events This Month
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                textAlign: "center",
                borderLeft: "4px solid #0F9D58",
                background: "#fff",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  color: "#0F9D58",
                }}
              >
                {monthStats.weeklyEvents}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Weekly Events This Month
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                textAlign: "center",
                borderLeft: "4px solid #DC0000",
                background: "#fff",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  color: "#DC0000",
                }}
              >
                {monthStats.monthlyEvents}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Monthly Events This Month
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                textAlign: "center",
                borderLeft: "4px solid #2196F3",
                background: "#fff",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  color: "#2196F3",
                }}
              >
                {todayStats.totalEvents}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Today's Available Events
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Beautiful Calendar */}
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          {/* Calendar Header */}
          <Box
            sx={{
              backgroundColor: "#f8f9fa",
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #dee2e6",
            }}
          >
            <Button
              onClick={goToPreviousMonth}
              variant="outlined"
              size="small"
              startIcon={<CalendarMonthIcon />}
            >
              Previous
            </Button>

            <Typography
              variant="h5"
              sx={{
                fontFamily: "'Bebas Neue', sans-serif",
                color: "#2c3e50",
                fontSize: "1.8rem",
              }}
            >
              {currentMonth.toLocaleString("default", {
                month: "long",
                year: "numeric",
              })}
            </Typography>

            <Button
              onClick={goToNextMonth}
              variant="outlined"
              size="small"
              endIcon={<DateRangeIcon />}
            >
              Next
            </Button>
          </Box>

          {/* REDESIGNED Calendar Grid */}
          <Box sx={{ p: 3, backgroundColor: "#fafbfc" }}>
            {/* Day Headers */}
            <Grid container spacing={1} sx={{ mb: 2 }}>
              {dayNames.map((day, index) => (
                <Grid item xs={12 / 7} key={index}>
                  <Box
                    sx={{
                      py: 1.5,
                      backgroundColor: "#e9ecef",
                      borderRadius: 1,
                      textAlign: "center",
                      border: "1px solid #dee2e6",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: "#495057",
                        fontSize: "0.85rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {day}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Calendar Days Grid */}
            <Grid container spacing={1}>
              {days.map((day, index) => {
                const isToday =
                  day.date.toDateString() === new Date().toDateString();
                const isSelected =
                  dateWasSelected &&
                  day.date.toDateString() === selectedDate.toDateString();

                return (
                  <Grid item xs={12 / 7} key={index}>
                    <Paper
                      elevation={isSelected ? 4 : 1}
                      sx={{
                        minHeight: "120px",
                        p: 2,
                        cursor: "pointer",
                        backgroundColor:
                          isToday && isSelected
                            ? "#e3f2fd"
                            : isToday
                            ? "#fff3e0"
                            : isSelected
                            ? "#e8f5e9"
                            : day.isCurrentMonth
                            ? "white"
                            : "#f8f9fa",
                        border: isToday
                          ? "2px solid #ff9800"
                          : isSelected
                          ? "2px solid #4caf50"
                          : "1px solid #e0e0e0",
                        borderRadius: 2,
                        transition: "all 0.2s ease-in-out",
                        opacity: day.isCurrentMonth ? 1 : 0.7,
                        "&:hover": {
                          backgroundColor: isSelected ? "#e8f5e9" : "#f5f5f5",
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        },
                      }}
                      onClick={() => handleDateSelect(day.date)}
                    >
                      {/* Date Number */}
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: isToday ? 700 : 500,
                          color: isToday
                            ? "#ff9800"
                            : isSelected
                            ? "#4caf50"
                            : day.isCurrentMonth
                            ? "#333"
                            : "#999",
                          mb: 1,
                          fontSize: "1.1rem",
                        }}
                      >
                        {day.date.getDate()}
                      </Typography>

                      {/* Event Buttons */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        {day.events.map((event, idx) => (
                          <Button
                            key={idx}
                            variant="contained"
                            size="small"
                            fullWidth
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEventSelect(event);
                            }}
                            sx={{
                              py: 0.5,
                              px: 1,
                              minHeight: "28px",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              borderRadius: 1.5,
                              textTransform: "none",
                              backgroundColor:
                                event === "Daily"
                                  ? "#800000"
                                  : event === "Weekly"
                                  ? "#0F9D58"
                                  : "#DC0000",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                              "&:hover": {
                                backgroundColor:
                                  event === "Daily"
                                    ? "#600000"
                                    : event === "Weekly"
                                    ? "#0A7A45"
                                    : "#A50000",
                                transform: "scale(1.02)",
                              },
                            }}
                          >
                            {event}
                          </Button>
                        ))}
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* Simple Legend */}
          <Box
            sx={{
              p: 2,
              backgroundColor: "#f8f9fa",
              borderTop: "1px solid #dee2e6",
            }}
          >
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="center"
            >
              <Grid item>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Event Types:
                </Typography>
              </Grid>
              <Grid item>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      backgroundColor: "#800000",
                      borderRadius: "50%",
                    }}
                  />
                  <Typography variant="body2">Daily</Typography>
                </Box>
              </Grid>
              <Grid item>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      backgroundColor: "#0F9D58",
                      borderRadius: "50%",
                    }}
                  />
                  <Typography variant="body2">Weekly</Typography>
                </Box>
              </Grid>
              <Grid item>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      backgroundColor: "#DC0000",
                      borderRadius: "50%",
                    }}
                  />
                  <Typography variant="body2">Monthly</Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    );
  };

  // Render weekly event form
  const renderWeeklyEventForm = () => {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Weekly Event Form
        </Typography>
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            This is the weekly event form. It will be implemented in the future.
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            Weekly reports are being developed. You'll be able to submit weekly
            data here soon.
          </Alert>
        </Paper>
      </Box>
    );
  };

  // Render monthly event form
  const renderMonthlyEventForm = () => {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Monthly Event Form
        </Typography>
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            This is the monthly event form. It will be implemented in the
            future.
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            Monthly reports are being developed. You'll be able to submit
            monthly data here soon.
          </Alert>
        </Paper>
      </Box>
    );
  };

  // Get form content based on selected event type
  const getEventFormContent = () => {
    switch (selectedEvent) {
      case "Daily":
        return (
          <Box>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
              {getCurrentSteps().map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {getStepContent(activeStep)}
          </Box>
        );
      case "Weekly":
        return renderWeeklyEventForm();
      case "Monthly":
        return renderMonthlyEventForm();
      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Box
        sx={{
          backgroundColor: "#fff",
          borderBottom: "3px solid #800000",
          p: 3,
          mb: 3,
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: "2.5rem",
            color: "#2c3e50",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <EditCalendarIcon
            sx={{
              mr: 2,
              color: "#800000",
              fontSize: "2.5rem",
            }}
          />
          6MS FORMS CALENDAR SYSTEM
        </Typography>
      </Box>
      {!showForms && !isLoading ? (
        renderCalendar()
      ) : isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "50vh",
          }}
        >
          <CircularProgress size={60} thickness={4} />
        </Box>
      ) : (
        <Box>
          <Box sx={{ mb: 3, display: "flex", alignItems: "center" }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                setShowForms(false);
                setSelectedEvent(null);
                window.location.reload();
              }}
              sx={{ mr: 2 }}
            >
              Back to Calendar
            </Button>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: "1.7rem",
                color: "black",
              }}
            >
              {selectedEvent} Event - {selectedDate.toLocaleDateString()}
            </Typography>
            <Box sx={{ marginLeft: "auto" }}>
              <Button variant="outlined" onClick={goToViewPage}>
                View All Records
              </Button>
            </Box>
          </Box>

          {getEventFormContent()}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button
              onClick={() => {
                setShowForms(false);
                window.location.reload();
              }}
              variant="outlined"
            >
              Cancel
            </Button>

            <Box>
              {activeStep > 0 && selectedEvent === "Daily" ? (
                <Button onClick={handleBack} sx={{ mr: 1 }}>
                  Back
                </Button>
              ) : null}

              {activeStep < getCurrentSteps().length - 1 &&
              selectedEvent === "Daily" ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={openConfirmationDialog}
                >
                  Submit
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      )}
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 0,
            minWidth: "600px",
            maxHeight: "80vh",
            overflowY: "auto",
            boxShadow: 3,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            transition: "transform 0.3s ease-in-out",
            transform: confirmDialogOpen ? "scale(1)" : "scale(0.95)",
          },
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{ bgcolor: "primary.main", color: "white", fontWeight: "bold" }}
        >
          Confirm Submission
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {renderConfirmationDialogContent()}
        </DialogContent>
        <DialogActions
          sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            variant="outlined"
            sx={{
              borderColor: "grey.500",
              color: "grey.700",
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmedSubmit}
            autoFocus
            variant="contained"
            color="primary"
            sx={{
              bgcolor: "primary.dark",
              "&:hover": { bgcolor: "primary.main" },
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      {/* New View Records Dialog */}
      <Dialog
        open={viewRecordsDialogOpen}
        onClose={() => setViewRecordsDialogOpen(false)}
        aria-labelledby="view-records-dialog-title"
        aria-describedby="view-records-dialog-description"
        TransitionComponent={Fade}
        transitionDuration={300}
        PaperProps={{
          elevation: 8,
          sx: {
            borderRadius: 2,
            minWidth: { xs: "95%", sm: "450px", md: "550px" },
            maxHeight: "80vh",
            overflowY: "auto",
            backgroundColor: "#ffffff",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
            border: "1px solid rgba(0, 0, 0, 0.08)",
            transition: "all 0.3s ease-in-out",
          },
        }}
      >
        <Box sx={{ position: "relative" }}>
          <DialogTitle
            id="view-records-dialog-title"
            sx={{
              bgcolor: "primary.main",
              color: "white",
              py: 2.5,
              pl: 3,
              pr: 6,
              fontSize: { xs: "1.1rem", sm: "1.25rem" },
              fontWeight: 500,
              letterSpacing: "0.5px",
            }}
          >
            View Records
          </DialogTitle>

          <IconButton
            aria-label="close"
            onClick={() => setViewRecordsDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "white",
              opacity: 0.8,
              "&:hover": {
                opacity: 1,
                background: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ px: 3, py: 4 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <LaunchIcon sx={{ fontSize: 28, color: "primary.main", mr: 1.5 }} />
            <Typography
              variant="h6"
              component="div"
              sx={{ fontWeight: 500, color: "#424242" }}
            >
              External Navigation
            </Typography>
          </Box>

          <DialogContentText
            id="view-records-dialog-description"
            sx={{
              color: "#616161",
              fontSize: "1rem",
              lineHeight: 1.6,
              mb: 1,
              pl: 0.5,
            }}
          >
            To view all records, a new tab needs to be opened. Continue?
          </DialogContentText>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: "#f8f8f8",
            borderTop: "1px solid",
            borderColor: "divider",
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Button
            onClick={() => setViewRecordsDialogOpen(false)}
            variant="outlined"
            sx={{
              borderColor: "grey.500",
              color: "grey.700",
              px: 3,
              py: 1,
              fontWeight: 500,
              textTransform: "none",
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleViewRecordsConfirm}
            variant="contained"
            color="primary"
            startIcon={<LaunchIcon />}
            sx={{
              bgcolor: "primary.dark",
              color: "white",
              px: 3,
              py: 1,
              fontWeight: 500,
              textTransform: "none",
              "&:hover": { bgcolor: "primary.main" },
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        TransitionComponent={Fade}
        transitionDuration={300}
        PaperProps={{
          elevation: 8,
          sx: {
            borderRadius: 2,
            minWidth: { xs: "95%", sm: "400px", md: "450px" },
            backgroundColor: "#ffffff",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
            border: "1px solid rgba(0, 0, 0, 0.08)",
            transition: "all 0.3s ease-in-out",
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ position: "relative" }}>
          <DialogTitle
            id="alert-dialog-title"
            sx={{
              bgcolor: "error.main",
              color: "white",
              py: 2,
              pl: 3,
              pr: 6,
              fontSize: { xs: "1.1rem", sm: "1.2rem" },
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <ErrorOutlineIcon />
            {alertTitle}
          </DialogTitle>

          <IconButton
            aria-label="close"
            onClick={() => setAlertOpen(false)}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "white",
              opacity: 0.8,
              "&:hover": {
                opacity: 1,
                background: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ px: 3, py: 3 }}>
          <DialogContentText
            id="alert-dialog-description"
            sx={{
              color: "#616161",
              fontSize: "1rem",
              lineHeight: 1.6,
              mb: 1,
            }}
          >
            {alertMessage}
          </DialogContentText>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            backgroundColor: "#f8f8f8",
            borderTop: "1px solid",
            borderColor: "divider",
            justifyContent: "center",
          }}
        >
          <Button
            onClick={() => setAlertOpen(false)}
            variant="contained"
            color="primary"
            sx={{
              bgcolor: "primary.dark",
              color: "white",
              px: 4,
              py: 1,
              fontWeight: 500,
              textTransform: "none",
              "&:hover": { bgcolor: "primary.main" },
            }}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FormList;
